import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import API from '../api';
import usePermissions from '../hooks/usePermissions';
import FileDropZone from '../components/FileDropZone';
import { printedByLabel } from '../utils/userLabel';
import { logPrint } from '../utils/eventLog';

// ══════════════════════════════════════════════════════════════════════════
// 회계관리 — 2026-09 신규 구현. 경리나라(웹케시)/더존 등 중소기업용 경리 SaaS의 공통 구성
// (계정과목 → 거래처 → 통장/카드 → 매출·매입(지출) → 전표(분개) → 재무제표/부가세신고자료)을
// 참조해 9개 탭으로 구성했다. 다른 메뉴(자산관리 Assets.js, 계약관리 Contracts.js)와 동일한
// 화면 규칙(색상/버튼/표/모달 스타일, 상단탭, 컬럼명 클릭정렬)을 그대로 따른다.
// ══════════════════════════════════════════════════════════════════════════

const C = { primary: '#0F6E56', border: '#e5e7eb', bg: '#f8fafc', text: '#333', sub: '#888' };
const btn = (bg, color = '#fff') => ({ background: bg, color, border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });
const smallBtn = (bg, color = '#fff') => ({ background: bg, color, border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });
const outlineBtn = { background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const input = { padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
const th = { padding: '10px 10px', fontSize: 13, fontWeight: 600, color: '#666', background: '#f8fafc', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid #e5e7eb' };
const td = { padding: '9px 10px', fontSize: 13.5, color: C.text, borderTop: '1px solid #f0f0f0', textAlign: 'center' };
const labelStyle = { fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 };
const formInput = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12, outline: 'none' };

const moneyFmt = (n) => (n || n === 0) ? Number(n).toLocaleString('ko-KR') : '0';
const onlyDigits = (s) => String(s || '').replace(/[^\d]/g, '');
const todayStr = () => new Date().toISOString().slice(0, 10);

function Section({ title, action, headerSlot, children }) {
  // headerSlot(DOM 노드)이 주어지면 action을 인라인(탭 아래) 대신 그 노드로 포탈해
  // 탭바 위쪽(우측상단)에 렌더링한다(2026-09 요청: 조회/등록 등 컨트롤을 탭 위쪽으로 이동).
  // 컨텍스트성(하위선택 종속) 섹션은 headerSlot을 넘기지 않아 기존 인라인 동작을 유지한다.
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
      {(title || (action && !headerSlot)) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
          {!headerSlot && action}
        </div>
      )}
      {headerSlot && action ? createPortal(action, headerSlot) : null}
      {children}
    </div>
  );
}

function useSort(list, defaultKey = null) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => { if (!key) return; if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } };
  const sorted = useMemo(() => {
    if (!sortKey) return list;
    const copy = [...list];
    copy.sort((a, b) => {
      let va = a?.[sortKey], vb = b?.[sortKey];
      if (va == null) va = ''; if (vb == null) vb = '';
      const na = Number(va), nb = Number(vb);
      const cmp = (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) ? na - nb : String(va).localeCompare(String(vb), 'ko');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [list, sortKey, sortDir]);
  return { sorted, sortKey, sortDir, toggleSort };
}
function Sortable({ label, sortKey, activeKey, dir, onClick }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)} style={{ cursor: 'pointer', userSelect: 'none', color: active ? C.primary : undefined, fontWeight: active ? 700 : undefined }}>
      {label}{active ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </span>
  );
}

function Modal({ title, width = 480, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, width, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', padding: 24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{title}</div>
          <span onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#999' }}>✕</span>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div style={{ marginBottom: 12 }}>{label && <label style={labelStyle}>{label}</label>}{children}</div>;
}

const TABS = [
  { key: 'dashboard', label: '대시보드' },
  { key: 'sales', label: '매출관리' },
  { key: 'purchases', label: '매입/지출관리' },
  { key: 'po', label: '발주관리' },
  { key: 'documents', label: '거래문서함' },
  { key: 'cost_analysis', label: '원가분석' },
  { key: 'banks', label: '통장/카드관리' },
  { key: 'partners', label: '거래처관리' },
  { key: 'vouchers', label: '전표관리' },
  { key: 'accounts', label: '계정과목관리' },
  { key: 'reports', label: '재무제표' },
  { key: 'vat', label: '부가세신고자료' },
];
// 브랜드 버튼(전체보기 제외)을 선택했을 때는 통장/카드관리·부가세신고자료는 전사 공통 데이터라
// 브랜드별로 나뉘지 않으므로 숨기고 전체보기에서만 보여준다(요청 7).
const BRAND_ONLY_HIDDEN_TABS = ['banks', 'vat'];

export default function Accounting() {
  const { canWrite, hasDetail } = usePermissions();
  const [tab, setTab] = useState('dashboard');
  const [partners, setPartners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const canManage = canWrite('accounting');
  // 탭별 컨트롤(조회/등록 등)을 탭바 위쪽 우측상단에 렌더링하기 위한 포탈 타겟(2026-09 요청).
  const [actionSlot, setActionSlot] = useState(null);

  // 2026-10 요청: "출고 매출 보정 버튼 위치를 브랜드관리 왼쪽으로 이동" — 원래 대시보드 탭 본문
  // 우측상단에 있던 버튼을 상단 헤더의 브랜드관리 버튼 옆(왼쪽)으로 옮긴다. 핸들러/상태를 이
  // 최상위 컴포넌트로 끌어올리고, 보정 완료 후 대시보드 탭이 다시 불러오도록 reload tick을
  // 내려보낸다(대시보드 탭 자체의 brandParam 변경 감지 useEffect에 이 tick도 의존성으로 추가).
  const [syncingShipmentSales, setSyncingShipmentSales] = useState(false);
  const [dashboardReloadTick, setDashboardReloadTick] = useState(0);
  const syncShipmentSales = () => {
    if (!window.confirm('출고완료 처리된 건 중 매출관리에 아직 반영되지 않은 건을 찾아 자동으로 등록합니다.\n계속하시겠습니까?')) return;
    setSyncingShipmentSales(true);
    API.post('/accounting/sync-shipment-sales')
      .then(r => { alert(r.data.message || '완료되었습니다.'); setDashboardReloadTick(t => t + 1); })
      .catch(err => alert(err.response?.data?.message || '처리 중 오류가 발생했습니다.'))
      .finally(() => setSyncingShipmentSales(false));
  };

  // ── 브랜드별 회계 구분(2026-09 3차) ────────────────────────────────────────
  // scope: 'all'(전체보기) | 'common'(공통관리) | 브랜드 id(숫자) — 브랜드가 1개도 없으면 'all' 고정.
  const [brands, setBrands] = useState([]);
  const [scope, setScope] = useState('all');
  const [brandModal, setBrandModal] = useState(false);
  const loadBrands = () => API.get('/company-info/brands').then(r => setBrands(r.data || [])).catch(() => {});
  useEffect(() => { loadBrands(); }, []);

  // 특정 브랜드를 선택한 상태에서만 통장/카드관리·부가세신고자료 탭을 숨긴다.
  const isBrandScope = typeof scope === 'number';
  const visibleTabs = TABS.filter(t => !(isBrandScope && BRAND_ONLY_HIDDEN_TABS.includes(t.key)));
  useEffect(() => {
    if (isBrandScope && BRAND_ONLY_HIDDEN_TABS.includes(tab)) setTab('dashboard');
  }, [scope]); // eslint-disable-line

  // 각 탭 API 호출에 실어보낼 brand_id 쿼리파라미터 값 — 'all'이면 아예 보내지 않음(전체 합산).
  const brandParam = scope === 'all' ? undefined : (scope === 'common' ? 'none' : scope);

  // 여러 탭에서 공통으로 필요한 거래처/계정과목/통장 목록은 최상위에서 한 번 불러와 내려준다.
  const reloadCommon = () => {
    API.get('/accounting/partners').then(r => setPartners(r.data)).catch(() => {});
    API.get('/accounting/accounts').then(r => setAccounts(r.data)).catch(() => {});
    API.get('/accounting/bank-accounts').then(r => setBanks(r.data)).catch(() => {});
  };
  useEffect(() => { reloadCommon(); }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>💰 회계관리</h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>계정과목·거래처·통장/카드부터 매출·매입, 전표(분개), 재무제표·부가세신고자료까지 관리합니다.</p>
        </div>
        {/* 브랜드 선택 버튼줄: [브랜드1][브랜드2]...[공통관리][전체보기] + (대시보드 탭에서만) [브랜드관리] */}
        <div className="hdr-actions" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {brands.map(b => (
            <button key={b.id} className="keep" onClick={() => setScope(b.id)}
              style={scope === b.id ? smallBtn(C.primary) : { ...smallBtn('#fff', '#555'), border: '1px solid #ddd' }}>
              {b.name}
            </button>
          ))}
          {brands.length > 0 && (
            <button className="keep" onClick={() => setScope('common')}
              style={scope === 'common' ? smallBtn('#854F0B') : { ...smallBtn('#fff', '#854F0B'), border: '1px solid #854F0B' }}>
              공통관리
            </button>
          )}
          {brands.length > 0 && (
            <button className="keep" onClick={() => setScope('all')}
              style={scope === 'all' ? smallBtn('#333') : { ...smallBtn('#fff', '#333'), border: '1px solid #ccc' }}>
              전체보기
            </button>
          )}
          {tab === 'dashboard' && (
            <>
              <button onClick={syncShipmentSales} disabled={syncingShipmentSales}
                style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #d0d5dd', background: '#fff', color: '#475467', cursor: syncingShipmentSales ? 'default' : 'pointer' }}
                title="출고완료 처리됐지만 매출관리에 반영되지 않은 건을 찾아 보정합니다.">
                {syncingShipmentSales ? '보정 중...' : '출고 매출 보정'}
              </button>
              <button onClick={() => setBrandModal(true)} style={{ ...smallBtn('#fff', C.primary), border: `1px solid ${C.primary}` }}>브랜드관리</button>
            </>
          )}
          {/* 2026-09(20차) 요청 — 탭별 컨트롤(조회/등록 등)을 탭바 위 별도 줄이 아니라, 업무관리
              업무현황의 "+업무등록" 버튼처럼 페이지 상단 헤더줄(제목 옆 우측) 안으로 이동한다. */}
          {tab !== 'dashboard' && <div ref={setActionSlot} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} />}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 18, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              background: 'none',
              color: tab === t.key ? C.primary : '#888',
              borderBottom: tab === t.key ? `2px solid ${C.primary}` : '2px solid transparent',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab brandParam={brandParam} reloadTick={dashboardReloadTick} />}
      {tab === 'accounts' && <AccountsTab accounts={accounts} reload={reloadCommon} canManage={canManage && hasDetail('accounting', 'account_manage')} headerSlot={actionSlot} />}
      {tab === 'partners' && <PartnersTab partners={partners} reload={reloadCommon} canManage={canManage && hasDetail('accounting', 'partner_manage')} headerSlot={actionSlot} />}
      {tab === 'banks' && <BanksTab banks={banks} partners={partners} accounts={accounts} reload={reloadCommon} canManage={canManage} headerSlot={actionSlot} />}
      {tab === 'sales' && <SalesTab partners={partners} accounts={accounts} banks={banks} brands={brands} scope={scope} brandParam={brandParam} canManage={canManage} reloadCommon={reloadCommon} headerSlot={actionSlot} />}
      {tab === 'purchases' && <PurchasesTab partners={partners} accounts={accounts} banks={banks} brands={brands} scope={scope} brandParam={brandParam} canManage={canManage} reloadCommon={reloadCommon} headerSlot={actionSlot} />}
      {tab === 'po' && <PoTab partners={partners} accounts={accounts} brands={brands} scope={scope} brandParam={brandParam} canManage={canManage && hasDetail('accounting', 'po_manage')} canDelete={canManage && hasDetail('accounting', 'delete_allowed')} headerSlot={actionSlot} />}
      {tab === 'documents' && <DocumentsTab partners={partners} canManage={canManage && hasDetail('accounting', 'inbound_doc_manage')} canDelete={canManage && hasDetail('accounting', 'delete_allowed')} headerSlot={actionSlot} />}
      {tab === 'cost_analysis' && <CostAnalysisTab partners={partners} accounts={accounts} brands={brands} canManage={canManage && hasDetail('accounting', 'po_manage')} canDelete={canManage && hasDetail('accounting', 'delete_allowed')} headerSlot={actionSlot} />}
      {tab === 'vouchers' && <VouchersTab accounts={accounts} partners={partners} brands={brands} scope={scope} brandParam={brandParam} canManage={canManage && hasDetail('accounting', 'voucher_manage')} headerSlot={actionSlot} />}
      {tab === 'reports' && <ReportsTab brandParam={brandParam} headerSlot={actionSlot} />}
      {tab === 'vat' && <VatTab headerSlot={actionSlot} />}
      {brandModal && <BrandManageModal brands={brands} onClose={() => setBrandModal(false)} onChanged={loadBrands} />}
    </div>
  );
}

// 회계관리 화면 전용 "브랜드관리" 모달 — 환경설정 > 브랜드 설정과 동일한 /company-info/brands API를
// 그대로 재사용해 브랜드 추가는 물론, 이미 등록된 브랜드의 이름 수정/삭제까지 한 화면에서 처리한다
// (로고/문서번호 분야 등 상세설정은 계속 환경설정 > 브랜드 설정에서 관리).
function BrandManageModal({ brands, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // null | 'new' | 브랜드 id
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const startAdd = () => { setEditing('new'); setName(''); };
  const startEdit = (b) => { setEditing(b.id); setName(b.name); };
  const cancelEdit = () => { setEditing(null); setName(''); };

  const submit = async () => {
    if (!name.trim()) { alert('브랜드명을 입력해주세요.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      if (editing === 'new') await API.post('/company-info/brands', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await API.put(`/company-info/brands/${editing}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      cancelEdit();
      onChanged();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`'${b.name}' 브랜드를 삭제하시겠습니까?`)) return;
    try { await API.delete(`/company-info/brands/${b.id}`); onChanged(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Modal title="브랜드관리" width={440} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {brands.length === 0 && <div style={{ color: '#aaa', fontSize: 13 }}>등록된 브랜드가 없습니다.</div>}
        {brands.map(b => (
          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
            {editing === b.id
              ? <input style={{ ...formInput, flex: 1 }} value={name} onChange={e => setName(e.target.value)} autoFocus />
              : <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{b.name}</div>}
            {editing === b.id ? (
              <>
                <button style={smallBtn(C.primary)} onClick={submit} disabled={saving}>저장</button>
                <button style={smallBtn('#eee', '#555')} onClick={cancelEdit}>취소</button>
              </>
            ) : (
              <>
                <button style={smallBtn('#eee', '#555')} onClick={() => startEdit(b)}>수정</button>
                <button style={smallBtn('#DC2626')} onClick={() => remove(b)}>삭제</button>
              </>
            )}
          </div>
        ))}
      </div>

      {editing === 'new' ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input style={{ ...formInput, flex: 1, marginBottom: 0 }} placeholder="브랜드명" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <button style={btn(C.primary)} onClick={submit} disabled={saving}>{saving ? '저장중...' : '추가'}</button>
          <button style={outlineBtn} onClick={cancelEdit}>취소</button>
        </div>
      ) : (
        <button style={{ ...outlineBtn, width: '100%' }} onClick={startAdd}>+ 브랜드 추가</button>
      )}

      <div style={{ fontSize: 12.5, color: '#888', margin: '12px 0 0' }}>로고/문서번호 분야 등 상세 설정은 환경설정 &gt; 브랜드 설정에서 추가로 관리할 수 있습니다.</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button style={outlineBtn} onClick={onClose}>닫기</button>
      </div>
    </Modal>
  );
}

// 매출/매입/전표/발주서 등록·수정 폼에 넣는 공용 "브랜드" 선택 필드.
// 공통(브랜드 미지정)을 기본값으로 두되, 브랜드별 화면(scope가 특정 브랜드)에서 새로 등록할 때는
// 그 브랜드를 기본 선택해준다(요청 5: "회계를 잡을 때 브랜드와 공통 부분을 나누어서 잡음").
function BrandField({ brands, value, onChange }) {
  if (!brands || brands.length === 0) return null;
  return (
    <Field label="브랜드(선택, 미지정시 공통)">
      <select style={formInput} value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : '')}>
        <option value="">공통</option>
        {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </Field>
  );
}

// ══════════════════════════════════════════════════════════════
// 대시보드
// ══════════════════════════════════════════════════════════════
function DashboardTab({ brandParam, reloadTick }) {
  const [data, setData] = useState(null);
  const loadDashboard = () => { API.get('/accounting/dashboard', { params: { brand_id: brandParam } }).then(r => setData(r.data)).catch(() => {}); };
  // 2026-10 요청: "출고 매출 보정" 버튼을 상단 헤더(브랜드관리 왼쪽)로 옮기면서, 보정 완료 후
  // 대시보드를 다시 불러오는 트리거를 reloadTick prop으로 받는다(Accounting() 최상위에서 증가시킴).
  useEffect(() => { loadDashboard(); }, [brandParam, reloadTick]); // eslint-disable-line
  if (!data) return <Section>불러오는 중...</Section>;
  // 2026-09 요청: "상단 카드메뉴에서 좌측에 2026년 총매출과 매입을 추가" — 연도는
  // 백엔드 응답의 data.year(서버 현재 연도)를 그대로 라벨에 사용해 하드코딩을 피한다.
  const cards = [
    { label: `${data.year}년 총매출 (VAT포함)`, value: data.year_sales, color: '#0E4F8B', bg: '#DCEBFA' },
    { label: `${data.year}년 총매입 (VAT포함)`, value: data.year_purchases, color: '#6B3F0A', bg: '#F5E6CC' },
    { label: '이번달 매출 (VAT포함)', value: data.month_sales, color: '#185FA5', bg: '#E6F1FB' },
    { label: '이번달 매입/지출 (VAT포함)', value: data.month_purchases, color: '#854F0B', bg: '#FAEEDA' },
    { label: '미수금(수금대기)', value: data.unpaid_receivable, color: '#DC2626', bg: '#FEE2E2' },
    { label: '미지급금(지급대기)', value: data.unpaid_payable, color: '#B45309', bg: '#FEF3C7' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: c.bg, borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ fontSize: 13, color: c.color, fontWeight: 600, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{moneyFmt(c.value)}원</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Section title="최근 매출">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>매출일</th><th style={th}>거래처</th><th style={th}>품목</th><th style={th}>합계금액</th></tr></thead>
            <tbody>
              {(data.recent_sales || []).length === 0 && <tr><td colSpan={4} style={{ ...td, color: '#aaa', padding: 20 }}>데이터가 없습니다.</td></tr>}
              {(data.recent_sales || []).map(r => (
                <tr key={r.id}><td style={td}>{r.sale_date}</td><td style={td}>{r.partner_name || r.partner_name_manual || '-'}</td><td style={td}>{r.item_name || '-'}</td><td style={td}>{moneyFmt(r.total_amount)}원</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
        <Section title="최근 매입/지출">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>매입일</th><th style={th}>거래처</th><th style={th}>품목</th><th style={th}>합계금액</th></tr></thead>
            <tbody>
              {(data.recent_purchases || []).length === 0 && <tr><td colSpan={4} style={{ ...td, color: '#aaa', padding: 20 }}>데이터가 없습니다.</td></tr>}
              {(data.recent_purchases || []).map(r => (
                <tr key={r.id}><td style={td}>{r.purchase_date}</td><td style={td}>{r.partner_name || r.partner_name_manual || '-'}</td><td style={td}>{r.item_name || '-'}</td><td style={td}>{moneyFmt(r.total_amount)}원</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 계정과목관리
// ══════════════════════════════════════════════════════════════
const ACCOUNT_TYPES = ['자산', '부채', '자본', '수익', '비용'];
function AccountsTab({ accounts, reload, canManage, headerSlot }) {
  const [modal, setModal] = useState(null); // {mode, row}
  const { sorted, sortKey, sortDir, toggleSort } = useSort(accounts, 'sort_order');

  const save = async (form) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/accounts', form);
      else await API.put(`/accounting/accounts/${modal.row.id}`, form);
      setModal(null); reload();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm(`'${row.name}' 계정과목을 삭제하시겠습니까?`)) return;
    try { await API.delete(`/accounting/accounts/${row.id}`); reload(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  const half = Math.ceil(sorted.length / 2);
  const leftRows = sorted.slice(0, half);
  const rightRows = sorted.slice(half);

  const renderTable = (rows) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}><Sortable label="코드" sortKey="code" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
          <th style={th}><Sortable label="계정명" sortKey="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
          <th style={th}><Sortable label="분류" sortKey="account_type" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
          <th style={th}>재무제표 그룹</th>
          {canManage && <th style={th}>관리</th>}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={canManage ? 5 : 4} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 계정과목이 없습니다.</td></tr>}
        {rows.map(a => (
          <tr key={a.id}>
            <td style={td}>{a.code || '-'}</td>
            <td style={td}>{a.name}</td>
            <td style={td}>{a.account_type}</td>
            <td style={td}>{a.category || '-'}</td>
            {canManage && <td style={td}>
              <button style={smallBtn('#eee', '#555')} onClick={() => setModal({ mode: 'edit', row: a })}>수정</button>{' '}
              <button style={smallBtn('#DC2626')} onClick={() => remove(a)}>삭제</button>
            </td>}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <Section headerSlot={headerSlot} action={canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 계정과목 등록</button>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {renderTable(leftRows)}
        {renderTable(rightRows)}
      </div>
      {modal && (
        <AccountModal mode={modal.mode} row={modal.row} onClose={() => setModal(null)} onSave={save} />
      )}
    </Section>
  );
}
function AccountModal({ mode, row, onClose, onSave }) {
  const [form, setForm] = useState({ code: row?.code || '', name: row?.name || '', account_type: row?.account_type || '자산', category: row?.category || '' });
  return (
    <Modal title={mode === 'add' ? '계정과목 등록' : '계정과목 수정'} width={420} onClose={onClose}>
      <Field label="계정코드(선택)"><input style={formInput} value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} /></Field>
      <Field label="계정명"><input style={formInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="분류">
        <select style={formInput} value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })}>
          {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="재무제표 그룹(선택, 예: 판매비와관리비)"><input style={formInput} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={() => onSave(form)}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 거래처관리
// ══════════════════════════════════════════════════════════════
function PartnersTab({ partners, reload, canManage, headerSlot }) {
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState('');
  const filtered = partners.filter(p => !q || p.partner_name.includes(q) || (p.biz_no || '').includes(q));
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered, 'partner_name');

  const save = async (form) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/partners', form);
      else await API.put(`/accounting/partners/${modal.row.id}`, form);
      setModal(null); reload();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm(`'${row.partner_name}' 거래처를 삭제하시겠습니까?`)) return;
    try { await API.delete(`/accounting/partners/${row.id}`); reload(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section headerSlot={headerSlot} action={
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={input} placeholder="거래처명/사업자번호 검색" value={q} onChange={e => setQ(e.target.value)} />
        {canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 거래처 등록</button>}
      </div>
    }>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}><Sortable label="거래처명" sortKey="partner_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}>구분</th><th style={th}>사업자번호</th><th style={th}>대표자</th><th style={th}>연락처</th><th style={th}>담당/은행정보</th>
            {canManage && <th style={th}>관리</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={7} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 거래처가 없습니다.</td></tr>}
          {sorted.map(p => (
            <tr key={p.id}>
              <td style={td}>{p.partner_name}</td>
              <td style={td}>{p.partner_type}</td>
              <td style={td}>{p.biz_no || '-'}</td>
              <td style={td}>{p.ceo_name || '-'}</td>
              <td style={td}>{p.phone || '-'}</td>
              <td style={td}>{p.bank_info || '-'}</td>
              {canManage && <td style={td}>
                <button style={smallBtn('#eee', '#555')} onClick={() => setModal({ mode: 'edit', row: p })}>수정</button>{' '}
                <button style={smallBtn('#DC2626')} onClick={() => remove(p)}>삭제</button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {modal && <PartnerModal mode={modal.mode} row={modal.row} onClose={() => setModal(null)} onSave={save} />}
    </Section>
  );
}
function PartnerModal({ mode, row, onClose, onSave }) {
  const [form, setForm] = useState({
    partner_name: row?.partner_name || '', partner_type: row?.partner_type || '공통', biz_no: row?.biz_no || '',
    ceo_name: row?.ceo_name || '', biz_type: row?.biz_type || '', biz_item: row?.biz_item || '',
    phone: row?.phone || '', email: row?.email || '', address: row?.address || '', bank_info: row?.bank_info || '', memo: row?.memo || '',
  });
  return (
    <Modal title={mode === 'add' ? '거래처 등록' : '거래처 수정'} width={520} onClose={onClose}>
      <Field label="거래처명"><input style={formInput} value={form.partner_name} onChange={e => setForm({ ...form, partner_name: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="구분">
          <select style={formInput} value={form.partner_type} onChange={e => setForm({ ...form, partner_type: e.target.value })}>
            <option value="공통">공통</option><option value="매출처">매출처</option><option value="매입처">매입처</option>
          </select>
        </Field>
        <Field label="사업자번호"><input style={formInput} value={form.biz_no} onChange={e => setForm({ ...form, biz_no: e.target.value })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="대표자"><input style={formInput} value={form.ceo_name} onChange={e => setForm({ ...form, ceo_name: e.target.value })} /></Field>
        <Field label="연락처"><input style={formInput} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="업태"><input style={formInput} value={form.biz_type} onChange={e => setForm({ ...form, biz_type: e.target.value })} /></Field>
        <Field label="종목"><input style={formInput} value={form.biz_item} onChange={e => setForm({ ...form, biz_item: e.target.value })} /></Field>
      </div>
      <Field label="이메일"><input style={formInput} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
      <Field label="주소"><input style={formInput} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></Field>
      <Field label="은행/계좌(결제정보)"><input style={formInput} value={form.bank_info} onChange={e => setForm({ ...form, bank_info: e.target.value })} /></Field>
      <Field label="비고"><textarea style={{ ...formInput, minHeight: 60 }} value={form.memo} onChange={e => setForm({ ...form, memo: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={() => onSave(form)}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 통장/카드관리 + 거래내역
// ══════════════════════════════════════════════════════════════
function BanksTab({ banks, partners, accounts, reload, canManage, headerSlot }) {
  const [modal, setModal] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [txns, setTxns] = useState([]);
  const [txnModal, setTxnModal] = useState(null);

  const loadTxns = (bankId) => {
    API.get('/accounting/bank-transactions', { params: { bank_account_id: bankId } }).then(r => setTxns(r.data)).catch(() => {});
  };
  useEffect(() => { if (selectedBank) loadTxns(selectedBank.id); }, [selectedBank]);

  const saveBank = async (form) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/bank-accounts', form);
      else await API.put(`/accounting/bank-accounts/${modal.row.id}`, form);
      setModal(null); reload();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const removeBank = async (row) => {
    if (!window.confirm(`'${row.alias || row.bank_name}' 계좌를 삭제하시겠습니까?`)) return;
    try { await API.delete(`/accounting/bank-accounts/${row.id}`); reload(); if (selectedBank?.id === row.id) setSelectedBank(null); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const saveTxn = async (form) => {
    try {
      if (txnModal.mode === 'add') await API.post('/accounting/bank-transactions', form);
      else await API.put(`/accounting/bank-transactions/${txnModal.row.id}`, form);
      setTxnModal(null); loadTxns(selectedBank.id); reload();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const removeTxn = async (row) => {
    if (!window.confirm('이 거래내역을 삭제하시겠습니까?')) return;
    try { await API.delete(`/accounting/bank-transactions/${row.id}`); loadTxns(selectedBank.id); reload(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16 }}>
      <Section headerSlot={headerSlot} action={canManage && <button style={smallBtn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 등록</button>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {banks.length === 0 && <div style={{ color: '#aaa', fontSize: 13, padding: 12 }}>등록된 계좌가 없습니다.</div>}
          {banks.map(b => (
            <div key={b.id} onClick={() => setSelectedBank(b)}
              style={{ border: `1px solid ${selectedBank?.id === b.id ? C.primary : C.border}`, borderRadius: 10, padding: 12, cursor: 'pointer', background: selectedBank?.id === b.id ? '#E1F5EE' : '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{b.alias || b.bank_name}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{b.account_type}</div>
              </div>
              <div style={{ fontSize: 12.5, color: '#666', marginTop: 4 }}>{b.bank_name} {b.account_no || ''}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.primary, marginTop: 6 }}>{moneyFmt(b.current_balance)}원</div>
              {canManage && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  <button style={smallBtn('#eee', '#555')} onClick={() => setModal({ mode: 'edit', row: b })}>수정</button>
                  <button style={smallBtn('#DC2626')} onClick={() => removeBank(b)}>삭제</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>
      <Section title={selectedBank ? `${selectedBank.alias || selectedBank.bank_name} 거래내역` : '거래내역'}
        action={selectedBank && canManage && <button style={btn(C.primary)} onClick={() => setTxnModal({ mode: 'add' })}>+ 거래내역 입력</button>}>
        {!selectedBank && <div style={{ color: '#aaa', padding: 20 }}>좌측에서 계좌를 선택해주세요.</div>}
        {selectedBank && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>거래일</th><th style={th}>구분</th><th style={th}>거래처</th><th style={th}>금액</th><th style={th}>적요</th>{canManage && <th style={th}>관리</th>}</tr></thead>
            <tbody>
              {txns.length === 0 && <tr><td colSpan={6} style={{ ...td, color: '#aaa', padding: 20 }}>거래내역이 없습니다.</td></tr>}
              {txns.map(t => (
                <tr key={t.id}>
                  <td style={td}>{t.txn_date}</td>
                  <td style={{ ...td, color: t.txn_type === '입금' ? '#0F6E56' : '#DC2626', fontWeight: 700 }}>{t.txn_type}</td>
                  <td style={td}>{t.partner_name || t.partner_name_manual || '-'}</td>
                  <td style={td}>{moneyFmt(t.amount)}원</td>
                  <td style={td}>{t.description || '-'}</td>
                  {canManage && <td style={td}>
                    <button style={smallBtn('#eee', '#555')} onClick={() => setTxnModal({ mode: 'edit', row: t })}>수정</button>{' '}
                    <button style={smallBtn('#DC2626')} onClick={() => removeTxn(t)}>삭제</button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {modal && <BankModal mode={modal.mode} row={modal.row} onClose={() => setModal(null)} onSave={saveBank} />}
      {txnModal && <TxnModal mode={txnModal.mode} row={txnModal.row} bank={selectedBank} partners={partners} accounts={accounts} onClose={() => setTxnModal(null)} onSave={saveTxn} />}
    </div>
  );
}
function BankModal({ mode, row, onClose, onSave }) {
  const [form, setForm] = useState({
    account_type: row?.account_type || '통장', bank_name: row?.bank_name || '', account_no: row?.account_no || '',
    alias: row?.alias || '', account_holder: row?.account_holder || '', opening_balance: row?.opening_balance || 0,
    opening_date: row?.opening_date || todayStr(), memo: row?.memo || '',
  });
  return (
    <Modal title={mode === 'add' ? '통장/카드 등록' : '통장/카드 수정'} width={440} onClose={onClose}>
      <Field label="구분">
        <select style={formInput} value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })}>
          <option value="통장">통장</option><option value="카드">카드</option><option value="현금">현금</option>
        </select>
      </Field>
      <Field label="은행명/카드사명"><input style={formInput} value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} /></Field>
      <Field label="계좌번호/카드번호"><input style={formInput} value={form.account_no} onChange={e => setForm({ ...form, account_no: e.target.value })} /></Field>
      <Field label="별칭(예: 주거래통장)"><input style={formInput} value={form.alias} onChange={e => setForm({ ...form, alias: e.target.value })} /></Field>
      <Field label="예금주"><input style={formInput} value={form.account_holder} onChange={e => setForm({ ...form, account_holder: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="기초잔액"><input style={formInput} value={moneyFmt(form.opening_balance)} onChange={e => setForm({ ...form, opening_balance: onlyDigits(e.target.value) })} /></Field>
        <Field label="등록일"><input type="date" style={formInput} value={form.opening_date} onChange={e => setForm({ ...form, opening_date: e.target.value })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={() => onSave(form)}>저장</button>
      </div>
    </Modal>
  );
}
function TxnModal({ mode, row, bank, partners, accounts, onClose, onSave }) {
  const [form, setForm] = useState({
    bank_account_id: bank.id, txn_date: row?.txn_date || todayStr(), txn_type: row?.txn_type || '입금',
    amount: row?.amount || 0, partner_id: row?.partner_id || '', partner_name_manual: row?.partner_name_manual || '',
    account_id: row?.account_id || '', description: row?.description || '',
  });
  return (
    <Modal title={mode === 'add' ? '거래내역 입력' : '거래내역 수정'} width={440} onClose={onClose}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="거래일"><input type="date" style={formInput} value={form.txn_date} onChange={e => setForm({ ...form, txn_date: e.target.value })} /></Field>
        <Field label="구분">
          <select style={formInput} value={form.txn_type} onChange={e => setForm({ ...form, txn_type: e.target.value })}>
            <option value="입금">입금</option><option value="출금">출금</option>
          </select>
        </Field>
      </div>
      <Field label="금액"><input style={formInput} value={moneyFmt(form.amount)} onChange={e => setForm({ ...form, amount: onlyDigits(e.target.value) })} /></Field>
      <Field label="거래처(등록된 거래처 선택, 또는 아래 직접입력)">
        <select style={formInput} value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
          <option value="">선택안함</option>
          {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name}</option>)}
        </select>
      </Field>
      {!form.partner_id && <Field label="거래처 직접입력"><input style={formInput} value={form.partner_name_manual} onChange={e => setForm({ ...form, partner_name_manual: e.target.value })} /></Field>}
      <Field label="계정과목(선택)">
        <select style={formInput} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })}>
          <option value="">선택안함</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <Field label="적요"><input style={formInput} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={() => onSave(form)}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 매출관리
// ══════════════════════════════════════════════════════════════
function SalesTab({ partners, accounts, banks, brands, scope, brandParam, canManage, reloadCommon, headerSlot }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState({ from_date: '', to_date: '', q: '' });
  const load = () => API.get('/accounting/sales', { params: { ...filter, brand_id: brandParam } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [brandParam]); // eslint-disable-line
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'sale_date');

  const save = async (formData) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/sales', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await API.put(`/accounting/sales/${modal.row.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModal(null); load(); reloadCommon();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm('이 매출건을 삭제하시겠습니까?')) return;
    try { await API.delete(`/accounting/sales/${row.id}`); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section headerSlot={headerSlot} action={
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="date" style={input} value={filter.from_date} onChange={e => setFilter({ ...filter, from_date: e.target.value })} />
        <input type="date" style={input} value={filter.to_date} onChange={e => setFilter({ ...filter, to_date: e.target.value })} />
        <button style={outlineBtn} onClick={load}>조회</button>
        {canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 매출등록</button>}
      </div>
    }>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}><Sortable label="매출일" sortKey="sale_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}>거래처</th><th style={th}>품목</th><th style={th}>공급가액</th><th style={th}>세액</th><th style={th}>합계</th>
            <th style={th}>세금계산서</th><th style={th}>수금상태</th>{canManage && <th style={th}>관리</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={9} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 매출이 없습니다.</td></tr>}
          {sorted.map(r => (
            <tr key={r.id}>
              <td style={td}>{r.sale_date}</td>
              <td style={td}>{r.partner_name || r.partner_name_manual || '-'}</td>
              <td style={td}>{r.item_name || '-'}</td>
              <td style={td}>{moneyFmt(r.supply_amount)}</td>
              <td style={td}>{moneyFmt(r.tax_amount)}</td>
              <td style={td}>{moneyFmt(r.total_amount)}</td>
              <td style={td}>{r.tax_invoice_status}</td>
              <td style={{ ...td, color: r.collection_status === '수금완료' ? '#0F6E56' : '#DC2626', fontWeight: 700 }}>{r.collection_status}</td>
              {canManage && <td style={td}>
                <button style={smallBtn('#eee', '#555')} onClick={() => setModal({ mode: 'edit', row: r })}>수정</button>{' '}
                <button style={smallBtn('#DC2626')} onClick={() => remove(r)}>삭제</button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {modal && <SaleModal mode={modal.mode} row={modal.row} partners={partners} accounts={accounts} banks={banks} brands={brands} defaultBrandId={typeof scope === 'number' ? scope : ''} onClose={() => setModal(null)} onSave={save} />}
    </Section>
  );
}
function SaleModal({ mode, row, partners, accounts, banks, brands, defaultBrandId, onClose, onSave }) {
  const [form, setForm] = useState({
    sale_date: row?.sale_date || todayStr(), partner_id: row?.partner_id || '', partner_name_manual: row?.partner_name_manual || '',
    item_name: row?.item_name || '', qty: row?.qty || '', unit_price: row?.unit_price || '',
    supply_amount: row?.supply_amount || '', tax_amount: row?.tax_amount || '', total_amount: row?.total_amount || '',
    tax_invoice_status: row?.tax_invoice_status || '미발행', tax_invoice_date: row?.tax_invoice_date || '',
    collection_status: row?.collection_status || '미수', collected_amount: row?.collected_amount || 0, collect_date: row?.collect_date || '',
    bank_account_id: row?.bank_account_id || '', account_id: row?.account_id || '', memo: row?.memo || '',
    brand_id: row?.brand_id ?? defaultBrandId ?? '',
  });
  const [files, setFiles] = useState([]);
  const submit = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
    files.forEach(f => fd.append('files', f));
    onSave(fd);
  };
  return (
    <Modal title={mode === 'add' ? '매출등록' : '매출수정'} width={560} onClose={onClose}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="매출일"><input type="date" style={formInput} value={form.sale_date} onChange={e => setForm({ ...form, sale_date: e.target.value })} /></Field>
        <Field label="거래처">
          <select style={formInput} value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
            <option value="">직접입력</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name}</option>)}
          </select>
        </Field>
      </div>
      {!form.partner_id && <Field label="거래처 직접입력"><input style={formInput} value={form.partner_name_manual} onChange={e => setForm({ ...form, partner_name_manual: e.target.value })} /></Field>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="품목"><input style={formInput} value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} /></Field>
        <Field label="수량"><input style={formInput} value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} /></Field>
        <Field label="단가"><input style={formInput} value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="공급가액"><input style={formInput} value={form.supply_amount} onChange={e => setForm({ ...form, supply_amount: onlyDigits(e.target.value) })} /></Field>
        <Field label="세액"><input style={formInput} value={form.tax_amount} onChange={e => setForm({ ...form, tax_amount: onlyDigits(e.target.value) })} /></Field>
        <Field label="합계(비워두면 자동계산)"><input style={formInput} value={form.total_amount} onChange={e => setForm({ ...form, total_amount: onlyDigits(e.target.value) })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="세금계산서">
          <select style={formInput} value={form.tax_invoice_status} onChange={e => setForm({ ...form, tax_invoice_status: e.target.value })}>
            <option value="미발행">미발행</option><option value="발행">발행</option><option value="면세">면세</option>
          </select>
        </Field>
        <Field label="계산서 발행일"><input type="date" style={formInput} value={form.tax_invoice_date} onChange={e => setForm({ ...form, tax_invoice_date: e.target.value })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="수금상태">
          <select style={formInput} value={form.collection_status} onChange={e => setForm({ ...form, collection_status: e.target.value })}>
            <option value="미수">미수</option><option value="부분수금">부분수금</option><option value="수금완료">수금완료</option>
          </select>
        </Field>
        <Field label="수금계좌">
          <select style={formInput} value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })}>
            <option value="">선택안함</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.alias || b.bank_name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="매출 계정과목(선택, 기본: 제품매출)">
        <select style={formInput} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })}>
          <option value="">선택안함</option>
          {accounts.filter(a => a.account_type === '수익').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <BrandField brands={brands} value={form.brand_id} onChange={v => setForm({ ...form, brand_id: v })} />
      <Field label="첨부(세금계산서/영수증 등)"><FileDropZone multiple fileNames={files.map(f => f.name)} onFiles={fs => setFiles(prev => [...prev, ...fs])} /></Field>
      <Field label="비고"><textarea style={{ ...formInput, minHeight: 50 }} value={form.memo} onChange={e => setForm({ ...form, memo: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={submit}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 매입/지출관리
// ══════════════════════════════════════════════════════════════
const EVIDENCE_TYPES = ['세금계산서', '계산서', '신용카드', '현금영수증', '간이영수증', '기타'];
function PurchasesTab({ partners, accounts, banks, brands, scope, brandParam, canManage, reloadCommon, headerSlot }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState({ from_date: '', to_date: '' });
  const load = () => API.get('/accounting/purchases', { params: { ...filter, brand_id: brandParam } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [brandParam]); // eslint-disable-line
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'purchase_date');

  const save = async (formData) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/purchases', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await API.put(`/accounting/purchases/${modal.row.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModal(null); load(); reloadCommon();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm('이 매입건을 삭제하시겠습니까?')) return;
    try { await API.delete(`/accounting/purchases/${row.id}`); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  // 2026-09 신규(요구사항 6) — 자재발주→발주서 입고완료 전환시 자동생성되는 매입행에 대해,
  // 실제 계좌이체를 완료한 뒤 이체확인증을 첨부하며 "결재완료"로 표시하는 전용 모달.
  const [payConfirmRow, setPayConfirmRow] = useState(null);
  const savePayConfirm = async (payDate, file) => {
    try {
      const fd = new FormData();
      fd.append('pay_date', payDate);
      if (file) fd.append('file', file);
      await API.patch(`/accounting/purchases/${payConfirmRow.id}/pay-confirm`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPayConfirmRow(null); load();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section headerSlot={headerSlot} action={
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="date" style={input} value={filter.from_date} onChange={e => setFilter({ ...filter, from_date: e.target.value })} />
        <input type="date" style={input} value={filter.to_date} onChange={e => setFilter({ ...filter, to_date: e.target.value })} />
        <button style={outlineBtn} onClick={load}>조회</button>
        {canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 매입/지출등록</button>}
      </div>
    }>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}><Sortable label="매입일" sortKey="purchase_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}>거래처</th><th style={th}>품목</th><th style={th}>공급가액</th><th style={th}>세액</th><th style={th}>합계</th>
            <th style={th}>증빙</th><th style={th}>지급상태</th>{canManage && <th style={th}>관리</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={9} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 매입/지출이 없습니다.</td></tr>}
          {sorted.map(r => (
            <tr key={r.id}>
              <td style={td}>{r.purchase_date}</td>
              <td style={td}>{r.partner_name || r.partner_name_manual || '-'}</td>
              <td style={td}>{r.item_name || '-'}</td>
              <td style={td}>{moneyFmt(r.supply_amount)}</td>
              <td style={td}>{moneyFmt(r.tax_amount)}</td>
              <td style={td}>{moneyFmt(r.total_amount)}</td>
              <td style={td}>{r.evidence_type}</td>
              <td style={{ ...td, color: r.payment_status === '지급완료' ? '#0F6E56' : '#DC2626', fontWeight: 700 }}>
                {r.payment_status}
                {canManage && r.payment_status !== '지급완료' && (
                  <button style={{ ...smallBtn('#0F6E56'), marginLeft: 6 }} onClick={() => setPayConfirmRow(r)}>이체확인증 등록</button>
                )}
              </td>
              {canManage && <td style={td}>
                <button style={smallBtn('#eee', '#555')} onClick={() => setModal({ mode: 'edit', row: r })}>수정</button>{' '}
                <button style={smallBtn('#DC2626')} onClick={() => remove(r)}>삭제</button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {modal && <PurchaseModal mode={modal.mode} row={modal.row} partners={partners} accounts={accounts} banks={banks} brands={brands} defaultBrandId={typeof scope === 'number' ? scope : ''} onClose={() => setModal(null)} onSave={save} />}
      {payConfirmRow && <PayConfirmModal row={payConfirmRow} onClose={() => setPayConfirmRow(null)} onSave={savePayConfirm} />}
    </Section>
  );
}
// 2026-09 신규(요구사항 6) — 이체확인증(계좌이체 확인서/증빙) 파일 + 지급일을 받아
// PATCH /purchases/:id/pay-confirm 로 넘긴다. 이 매입이 자재발주→발주서 입고완료 전환으로
// 자동생성된 것이면(source_type='purchase_order'), 재고관리 > 원자재 탭 목록의 "결재" 열에도
// 바로 "결재완료(YYMMDD)"로 반영된다(GET /material-orders의 JOIN 조회 — stock.js 참고).
function PayConfirmModal({ row, onClose, onSave }) {
  const [payDate, setPayDate] = useState(todayStr());
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => { setSaving(true); await onSave(payDate, file); setSaving(false); };
  return (
    <Modal title="이체확인증 등록(결재완료)" width={420} onClose={onClose}>
      <Field label="지급(이체)일자"><input type="date" style={formInput} value={payDate} onChange={e => setPayDate(e.target.value)} /></Field>
      <Field label="이체확인증 첨부"><input type="file" onChange={e => setFile(e.target.files?.[0] || null)} /></Field>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} disabled={saving} onClick={submit}>{saving ? '저장 중...' : '결재완료로 저장'}</button>
      </div>
    </Modal>
  );
}
function PurchaseModal({ mode, row, partners, accounts, banks, brands, defaultBrandId, onClose, onSave }) {
  const [form, setForm] = useState({
    purchase_date: row?.purchase_date || todayStr(), partner_id: row?.partner_id || '', partner_name_manual: row?.partner_name_manual || '',
    item_name: row?.item_name || '', qty: row?.qty || '', unit_price: row?.unit_price || '',
    supply_amount: row?.supply_amount || '', tax_amount: row?.tax_amount || '', total_amount: row?.total_amount || '',
    evidence_type: row?.evidence_type || '세금계산서', payment_status: row?.payment_status || '미지급',
    paid_amount: row?.paid_amount || 0, pay_date: row?.pay_date || '',
    bank_account_id: row?.bank_account_id || '', account_id: row?.account_id || '', memo: row?.memo || '',
    brand_id: row?.brand_id ?? defaultBrandId ?? '',
  });
  const [files, setFiles] = useState([]);
  const submit = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
    files.forEach(f => fd.append('files', f));
    onSave(fd);
  };
  return (
    <Modal title={mode === 'add' ? '매입/지출등록' : '매입/지출수정'} width={560} onClose={onClose}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="매입일"><input type="date" style={formInput} value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} /></Field>
        <Field label="거래처">
          <select style={formInput} value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
            <option value="">직접입력</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name}</option>)}
          </select>
        </Field>
      </div>
      {!form.partner_id && <Field label="거래처 직접입력"><input style={formInput} value={form.partner_name_manual} onChange={e => setForm({ ...form, partner_name_manual: e.target.value })} /></Field>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="품목"><input style={formInput} value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} /></Field>
        <Field label="수량"><input style={formInput} value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} /></Field>
        <Field label="단가"><input style={formInput} value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="공급가액"><input style={formInput} value={form.supply_amount} onChange={e => setForm({ ...form, supply_amount: onlyDigits(e.target.value) })} /></Field>
        <Field label="세액"><input style={formInput} value={form.tax_amount} onChange={e => setForm({ ...form, tax_amount: onlyDigits(e.target.value) })} /></Field>
        <Field label="합계(비워두면 자동계산)"><input style={formInput} value={form.total_amount} onChange={e => setForm({ ...form, total_amount: onlyDigits(e.target.value) })} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="증빙유형">
          <select style={formInput} value={form.evidence_type} onChange={e => setForm({ ...form, evidence_type: e.target.value })}>
            {EVIDENCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="지급상태">
          <select style={formInput} value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })}>
            <option value="미지급">미지급</option><option value="부분지급">부분지급</option><option value="지급완료">지급완료</option>
          </select>
        </Field>
      </div>
      <Field label="지급계좌">
        <select style={formInput} value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })}>
          <option value="">선택안함</option>
          {banks.map(b => <option key={b.id} value={b.id}>{b.alias || b.bank_name}</option>)}
        </select>
      </Field>
      <Field label="지출 계정과목(선택)">
        <select style={formInput} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })}>
          <option value="">선택안함</option>
          {accounts.filter(a => a.account_type === '비용').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <BrandField brands={brands} value={form.brand_id} onChange={v => setForm({ ...form, brand_id: v })} />
      <Field label="첨부(세금계산서/영수증 등)"><FileDropZone multiple fileNames={files.map(f => f.name)} onFiles={fs => setFiles(prev => [...prev, ...fs])} /></Field>
      <Field label="비고"><textarea style={{ ...formInput, minHeight: 50 }} value={form.memo} onChange={e => setForm({ ...form, memo: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={submit}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 전표(분개)관리
// ══════════════════════════════════════════════════════════════
function VouchersTab({ accounts, partners, brands, scope, brandParam, canManage, headerSlot }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const load = () => API.get('/accounting/vouchers', { params: { brand_id: brandParam } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [brandParam]); // eslint-disable-line
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'voucher_date');

  const openEdit = async (row) => {
    const r = await API.get(`/accounting/vouchers/${row.id}`);
    setModal({ mode: 'edit', row: r.data });
  };
  const save = async (form) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/vouchers', form);
      else await API.put(`/accounting/vouchers/${modal.row.id}`, form);
      setModal(null); load();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm(`전표 '${row.voucher_no}'를 삭제하시겠습니까?`)) return;
    try { await API.delete(`/accounting/vouchers/${row.id}`); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section headerSlot={headerSlot} action={canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 전표작성</button>}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}><Sortable label="전표일자" sortKey="voucher_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}>전표번호</th><th style={th}>구분</th><th style={th}>적요</th><th style={th}>차변합계</th><th style={th}>대변합계</th>
            {canManage && <th style={th}>관리</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={7} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 전표가 없습니다.</td></tr>}
          {sorted.map(r => (
            <tr key={r.id}>
              <td style={td}>{r.voucher_date}</td>
              <td style={{ ...td, cursor: 'pointer', color: C.primary }} onClick={() => openEdit(r)}>{r.voucher_no}</td>
              <td style={td}>{r.voucher_type}</td>
              <td style={td}>{r.description || '-'}</td>
              <td style={td}>{moneyFmt(r.debit_total)}</td>
              <td style={td}>{moneyFmt(r.credit_total)}</td>
              {canManage && <td style={td}>
                <button style={smallBtn('#eee', '#555')} onClick={() => openEdit(r)}>수정</button>{' '}
                <button style={smallBtn('#DC2626')} onClick={() => remove(r)}>삭제</button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {modal && <VoucherModal mode={modal.mode} row={modal.row} accounts={accounts} partners={partners} brands={brands} defaultBrandId={typeof scope === 'number' ? scope : ''} onClose={() => setModal(null)} onSave={save} />}
    </Section>
  );
}
function VoucherModal({ mode, row, accounts, partners, brands, defaultBrandId, onClose, onSave }) {
  const [voucherDate, setVoucherDate] = useState(row?.voucher_date || todayStr());
  const [voucherType, setVoucherType] = useState(row?.voucher_type || '대체');
  const [description, setDescription] = useState(row?.description || '');
  const [brandId, setBrandId] = useState(row?.brand_id ?? defaultBrandId ?? '');
  const [lines, setLines] = useState(row?.lines?.length ? row.lines.map(l => ({ side: l.side, account_id: l.account_id, amount: l.amount, partner_id: l.partner_id || '', description: l.description || '' }))
    : [{ side: '차변', account_id: '', amount: '', partner_id: '', description: '' }, { side: '대변', account_id: '', amount: '', partner_id: '', description: '' }]);

  const debitSum = lines.filter(l => l.side === '차변').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const creditSum = lines.filter(l => l.side === '대변').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const balanced = debitSum === creditSum && debitSum > 0;

  const updateLine = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = (side) => setLines(prev => [...prev, { side, account_id: '', amount: '', partner_id: '', description: '' }]);
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const submit = () => {
    if (!balanced) { alert('차변과 대변의 합계가 일치해야 합니다.'); return; }
    onSave({ voucher_date: voucherDate, voucher_type: voucherType, description, brand_id: brandId, lines: lines.filter(l => l.account_id && l.amount) });
  };

  return (
    <Modal title={mode === 'add' ? '전표작성' : '전표수정'} width={700} onClose={onClose}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="전표일자"><input type="date" style={formInput} value={voucherDate} onChange={e => setVoucherDate(e.target.value)} /></Field>
        <Field label="구분">
          <select style={formInput} value={voucherType} onChange={e => setVoucherType(e.target.value)}>
            <option value="대체">대체</option><option value="입금">입금</option><option value="출금">출금</option>
          </select>
        </Field>
        <Field label="적요"><input style={formInput} value={description} onChange={e => setDescription(e.target.value)} /></Field>
      </div>
      <BrandField brands={brands} value={brandId} onChange={setBrandId} />
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
        <thead><tr><th style={th}>구분</th><th style={th}>계정과목</th><th style={th}>거래처</th><th style={th}>금액</th><th style={th}>적요</th><th style={th}></th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td style={td}>
                <select style={{ ...input, width: 70 }} value={l.side} onChange={e => updateLine(i, { side: e.target.value })}>
                  <option value="차변">차변</option><option value="대변">대변</option>
                </select>
              </td>
              <td style={td}>
                <select style={{ ...input, width: 140 }} value={l.account_id} onChange={e => updateLine(i, { account_id: e.target.value })}>
                  <option value="">선택</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </td>
              <td style={td}>
                <select style={{ ...input, width: 110 }} value={l.partner_id} onChange={e => updateLine(i, { partner_id: e.target.value })}>
                  <option value="">선택안함</option>
                  {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name}</option>)}
                </select>
              </td>
              <td style={td}><input style={{ ...input, width: 100, textAlign: 'right' }} value={moneyFmt(l.amount)} onChange={e => updateLine(i, { amount: onlyDigits(e.target.value) })} /></td>
              <td style={td}><input style={{ ...input, width: 120 }} value={l.description} onChange={e => updateLine(i, { description: e.target.value })} /></td>
              <td style={td}><span style={{ cursor: 'pointer', color: '#DC2626' }} onClick={() => removeLine(i)}>✕</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button style={smallBtn('#eee', '#555')} onClick={() => addLine('차변')}>+ 차변 라인</button>
        <button style={smallBtn('#eee', '#555')} onClick={() => addLine('대변')}>+ 대변 라인</button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13, fontWeight: 700 }}>
        <span>차변합계: {moneyFmt(debitSum)}</span>
        <span>대변합계: {moneyFmt(creditSum)}</span>
        <span style={{ color: balanced ? '#0F6E56' : '#DC2626' }}>{balanced ? '일치' : '불일치'}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={submit}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 재무제표
// ══════════════════════════════════════════════════════════════
function ReportsTab({ brandParam, headerSlot }) {
  const [period, setPeriod] = useState({ from_date: `${new Date().getFullYear()}-01-01`, to_date: todayStr() });
  const [data, setData] = useState(null);
  const load = () => API.get('/accounting/financial-statements', { params: { ...period, brand_id: brandParam } }).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [brandParam]); // eslint-disable-line

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Section headerSlot={headerSlot} action={
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" style={input} value={period.from_date} onChange={e => setPeriod({ ...period, from_date: e.target.value })} />
          <input type="date" style={input} value={period.to_date} onChange={e => setPeriod({ ...period, to_date: e.target.value })} />
          <button style={outlineBtn} onClick={load}>조회</button>
        </div>
      }>
      </Section>
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section title="손익계산서">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>매출/수익</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <tbody>
                {data.income_statement.revenue.map(r => <tr key={r.id}><td style={td}>{r.name}</td><td style={{ ...td, textAlign: 'right' }}>{moneyFmt(r.balance)}</td></tr>)}
                <tr><td style={{ ...td, fontWeight: 700 }}>수익 합계</td><td style={{ ...td, fontWeight: 700, textAlign: 'right' }}>{moneyFmt(data.income_statement.total_revenue)}</td></tr>
              </tbody>
            </table>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>비용</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <tbody>
                {data.income_statement.expense.map(r => <tr key={r.id}><td style={td}>{r.name}</td><td style={{ ...td, textAlign: 'right' }}>{moneyFmt(r.balance)}</td></tr>)}
                <tr><td style={{ ...td, fontWeight: 700 }}>비용 합계</td><td style={{ ...td, fontWeight: 700, textAlign: 'right' }}>{moneyFmt(data.income_statement.total_expense)}</td></tr>
              </tbody>
            </table>
            <div style={{ fontSize: 16, fontWeight: 800, color: data.income_statement.net_income >= 0 ? C.primary : '#DC2626' }}>
              당기순이익: {moneyFmt(data.income_statement.net_income)}원
            </div>
          </Section>
          <Section title="재무상태표">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>자산</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <tbody>
                {data.balance_sheet.assets.map(r => <tr key={r.id}><td style={td}>{r.name}</td><td style={{ ...td, textAlign: 'right' }}>{moneyFmt(r.balance)}</td></tr>)}
                <tr><td style={{ ...td, fontWeight: 700 }}>자산 합계</td><td style={{ ...td, fontWeight: 700, textAlign: 'right' }}>{moneyFmt(data.balance_sheet.total_assets)}</td></tr>
              </tbody>
            </table>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>부채</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <tbody>
                {data.balance_sheet.liabilities.map(r => <tr key={r.id}><td style={td}>{r.name}</td><td style={{ ...td, textAlign: 'right' }}>{moneyFmt(r.balance)}</td></tr>)}
                <tr><td style={{ ...td, fontWeight: 700 }}>부채 합계</td><td style={{ ...td, fontWeight: 700, textAlign: 'right' }}>{moneyFmt(data.balance_sheet.total_liabilities)}</td></tr>
              </tbody>
            </table>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>자본</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {data.balance_sheet.equity.map(r => <tr key={r.id}><td style={td}>{r.name}</td><td style={{ ...td, textAlign: 'right' }}>{moneyFmt(r.balance)}</td></tr>)}
                <tr><td style={{ ...td, fontWeight: 700 }}>자본 합계</td><td style={{ ...td, fontWeight: 700, textAlign: 'right' }}>{moneyFmt(data.balance_sheet.total_equity)}</td></tr>
              </tbody>
            </table>
          </Section>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 부가세신고자료
// ══════════════════════════════════════════════════════════════
function VatTab({ headerSlot }) {
  const now = new Date();
  const [period, setPeriod] = useState({ from_date: `${now.getFullYear()}-01-01`, to_date: todayStr() });
  const [data, setData] = useState(null);
  const load = (p) => API.get('/accounting/vat-report', { params: p || period }).then(r => setData(r.data)).catch(e => alert(e?.response?.data?.message || '오류'));
  useEffect(() => { load(); }, []); // eslint-disable-line
  // 2026-10 요청: 상반기(1~6월)/하반기(7~12월)를 버튼 한 번으로 한번에 조회.
  const year = now.getFullYear();
  const setHalf = (half) => {
    const p = half === 'first'
      ? { from_date: `${year}-01-01`, to_date: `${year}-06-30` }
      : { from_date: `${year}-07-01`, to_date: `${year}-12-31` };
    setPeriod(p);
    load(p);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Section headerSlot={headerSlot} action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={outlineBtn} onClick={() => setHalf('first')}>상반기</button>
          <button style={outlineBtn} onClick={() => setHalf('second')}>하반기</button>
          <input type="date" style={input} value={period.from_date} onChange={e => setPeriod({ ...period, from_date: e.target.value })} />
          <input type="date" style={input} value={period.to_date} onChange={e => setPeriod({ ...period, to_date: e.target.value })} />
          <button style={outlineBtn} onClick={() => load()}>조회</button>
        </div>
      }>
      </Section>
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div style={{ background: '#E6F1FB', borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#185FA5', fontWeight: 600 }}>매출 (과세)</div>
              <div style={{ fontSize: 13, color: '#185FA5', fontWeight: 600 }}>{data.sales.cnt}건</div>
            </div>
            <div style={{ fontSize: 13, color: '#555' }}>공급가액: {moneyFmt(data.sales.supply)}원 / 세액: {moneyFmt(data.output_tax)}원</div>
          </div>
          <div style={{ background: '#FAEEDA', borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#854F0B', fontWeight: 600 }}>매입</div>
              <div style={{ fontSize: 13, color: '#854F0B', fontWeight: 600 }}>{data.purchases.cnt}건</div>
            </div>
            <div style={{ fontSize: 13, color: '#555' }}>공급가액: {moneyFmt(data.purchases.supply)}원 / 세액: {moneyFmt(data.input_tax)}원</div>
          </div>
          <div style={{ background: data.payable_tax >= 0 ? '#E1F5EE' : '#FEE2E2', borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 13, color: data.payable_tax >= 0 ? '#0F6E56' : '#DC2626', fontWeight: 600, marginBottom: 8 }}>
              {data.payable_tax >= 0 ? '납부예상세액' : '환급예상세액'}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: data.payable_tax >= 0 ? '#0F6E56' : '#DC2626' }}>{moneyFmt(Math.abs(data.payable_tax))}원</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>면세매출: {moneyFmt(data.exempt_sales.supply)}원 ({data.exempt_sales.cnt}건)</div>
          </div>
        </div>
      )}
      {/* 2026-10 요청: 합계 카드 아래에 매출/매입 상세내역 표시 */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section title="매출 상세내역">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>매출일</th><th style={th}>거래처</th><th style={th}>품목</th><th style={th}>공급가액</th><th style={th}>세액</th><th style={th}>합계</th></tr></thead>
              <tbody>
                {(data.sale_details || []).length === 0 && <tr><td colSpan={6} style={{ ...td, color: '#aaa', padding: 20 }}>데이터가 없습니다.</td></tr>}
                {(data.sale_details || []).map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.doc_date}</td><td style={td}>{r.partner_name || '-'}</td><td style={td}>{r.item_name || '-'}</td>
                    <td style={td}>{moneyFmt(r.supply_amount)}원</td><td style={td}>{moneyFmt(r.tax_amount)}원</td><td style={td}>{moneyFmt(r.total_amount)}원</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
          <Section title="매입 상세내역">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>매입일</th><th style={th}>거래처</th><th style={th}>품목</th><th style={th}>공급가액</th><th style={th}>세액</th><th style={th}>합계</th></tr></thead>
              <tbody>
                {(data.purchase_details || []).length === 0 && <tr><td colSpan={6} style={{ ...td, color: '#aaa', padding: 20 }}>데이터가 없습니다.</td></tr>}
                {(data.purchase_details || []).map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.doc_date}</td><td style={td}>{r.partner_name || '-'}</td><td style={td}>{r.item_name || '-'}</td>
                    <td style={td}>{moneyFmt(r.supply_amount)}원</td><td style={td}>{moneyFmt(r.tax_amount)}원</td><td style={td}>{moneyFmt(r.total_amount)}원</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 발주관리 — 2026-09(2차) 신규. 협력사에 발행하는 발주서. 상태를 "입고완료"로
// 바꾸면 매입/지출관리에 자동 반영된다(백엔드 PATCH /purchase-orders/:id/status).
// ══════════════════════════════════════════════════════════════
const PO_STATUS_COLOR = { '발주': '#888', '확정': '#185FA5', '입고완료': '#0F6E56', '취소': '#DC2626' };

function PoTab({ partners, accounts, brands, scope, brandParam, canManage, canDelete, headerSlot }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState({ from_date: '', to_date: '', status: '' });
  const load = () => API.get('/accounting/purchase-orders', { params: { ...filter, brand_id: brandParam } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [brandParam]); // eslint-disable-line
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'po_date');

  const save = async (form) => {
    try {
      if (modal.mode === 'add') await API.post('/accounting/purchase-orders', form);
      else await API.put(`/accounting/purchase-orders/${modal.row.id}`, form);
      setModal(null); load();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const changeStatus = async (row, status) => {
    if (status === '입고완료' && !window.confirm('입고완료로 변경하면 매입/지출관리에 자동으로 반영됩니다. 계속할까요?')) return;
    try { await API.patch(`/accounting/purchase-orders/${row.id}/status`, { status }); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm('이 발주서를 삭제하시겠습니까? (입고완료로 반영된 매입내역도 함께 삭제됩니다)')) return;
    try { await API.delete(`/accounting/purchase-orders/${row.id}`); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  return (
    <Section headerSlot={headerSlot} action={
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="date" style={input} value={filter.from_date} onChange={e => setFilter({ ...filter, from_date: e.target.value })} />
        <input type="date" style={input} value={filter.to_date} onChange={e => setFilter({ ...filter, to_date: e.target.value })} />
        <select style={input} value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
          <option value="">전체상태</option>
          <option value="발주">발주</option><option value="확정">확정</option><option value="입고완료">입고완료</option><option value="취소">취소</option>
        </select>
        <button style={outlineBtn} onClick={load}>조회</button>
        {canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 발주서등록</button>}
      </div>
    }>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}><Sortable label="발주번호" sortKey="po_no" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}><Sortable label="발주일" sortKey="po_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}>협력사</th><th style={th}>품목</th><th style={th}>수량</th><th style={th}>합계</th>
            <th style={th}>입고예정일</th><th style={th}>상태</th>{canManage && <th style={th}>관리</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={9} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 발주서가 없습니다.</td></tr>}
          {sorted.map(r => (
            <tr key={r.id}>
              <td style={td}>{r.po_no}</td>
              <td style={td}>{r.po_date}</td>
              <td style={td}>{r.partner_name || r.partner_name_manual || '-'}</td>
              <td style={td}>{r.item_name || '-'}</td>
              <td style={td}>{r.qty || '-'}</td>
              <td style={td}>{moneyFmt(r.total_amount)}</td>
              <td style={td}>{r.expected_date || '-'}</td>
              <td style={{ ...td, color: PO_STATUS_COLOR[r.status] || '#555', fontWeight: 700 }}>
                {canManage ? (
                  <select style={{ ...smallBtn('#fff', PO_STATUS_COLOR[r.status] || '#555'), border: '1px solid #ddd' }} value={r.status} onChange={e => changeStatus(r, e.target.value)}>
                    <option value="발주">발주</option><option value="확정">확정</option><option value="입고완료">입고완료</option><option value="취소">취소</option>
                  </select>
                ) : r.status}
              </td>
              {canManage && <td style={td}>
                <button style={smallBtn('#eee', '#555')} onClick={() => setModal({ mode: 'edit', row: r })}>수정</button>{' '}
                {canDelete && <button style={smallBtn('#DC2626')} onClick={() => remove(r)}>삭제</button>}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {modal && <PoModal mode={modal.mode} row={modal.row} partners={partners} accounts={accounts} brands={brands} defaultBrandId={typeof scope === 'number' ? scope : ''} onClose={() => setModal(null)} onSave={save} />}
    </Section>
  );
}
// 2026-10 요청(원가분석 상세내역 등록 개편) — 구분 드롭다운 선택지.
const PO_CATEGORY_OPTIONS = ['사내', '외주', '사내+외주', '용역'];
// 2026-10 요청(상세내역 등록 재개편) — 품목명은 아래 목록에서 선택하거나 직접 입력할 수 있다.
const ITEM_NAME_OPTIONS = ['설계', '자재', '출장', '물류', '가공', '조립', '부품', '소모품', '인건비', '기타'];
// 2026-10 요청(원가분석 종합분석) — 구분별 사용비용 집계 그룹. '사내+외주'/미구분은 기타 카드로 따로 집계.
const COST_GROUPS = [
  { key: 'in', label: '사내비용', color: '#2563EB', match: c => c === '사내' },
  { key: 'service', label: '용역비용', color: '#7C3AED', match: c => c === '용역' },
  { key: 'out', label: '외주비용', color: '#EA580C', match: c => c === '외주' },
];
// 소재 비중(g/cm³). 총중량(kg) = 부피(mm³) × 비중(g/cm³) / 1,000,000 × 수량.
const MATERIAL_DENSITY = { 'MC나일론': 1.15, 'SUS304': 7.93, 'AL6061': 2.70, 'AL5052': 2.68, 'SS275': 7.85 };
const MATERIAL_OPTIONS = Object.keys(MATERIAL_DENSITY);
// 규격 문자열에서 치수를 파싱한다. "Φ"(또는 φ/D) 표기가 있으면 환봉(지름×길이), 없으면
// 숫자 3개(가로×세로×높이)로 구성된 평판으로 간주한다. 모든 치수는 mm 기준.
function parseSpecDimensions(spec) {
  const s = String(spec || '').trim();
  if (!s) return null;
  const nums = (s.match(/[0-9]+(\.[0-9]+)?/g) || []).map(Number).filter(n => n > 0);
  if (/[ΦφD]/i.test(s)) {
    if (nums.length < 2) return null;
    return { shape: 'round', d: nums[0], l: nums[1] };
  }
  if (nums.length < 3) return null;
  return { shape: 'plate', w: nums[0], d: nums[1], h: nums[2] };
}
function calcTotalWeightKg(spec, material, qty) {
  const dim = parseSpecDimensions(spec);
  const density = MATERIAL_DENSITY[material];
  const q = Number(qty) || 0;
  if (!dim || !density || q <= 0) return null;
  const volumeMm3 = dim.shape === 'round' ? Math.PI * Math.pow(dim.d / 2, 2) * dim.l : dim.w * dim.d * dim.h;
  if (!(volumeMm3 > 0)) return null;
  const weightKg = (volumeMm3 * density / 1e6) * q;
  return Math.round(weightKg * 1000) / 1000;
}

function PoModal({ mode, row, partners, accounts, brands, defaultBrandId, titlePrefix, onClose, onSave }) {
  const [form, setForm] = useState({
    po_date: row?.po_date || todayStr(), partner_id: row?.partner_id || '', partner_name_manual: row?.partner_name_manual || '',
    item_name: row?.item_name || '', spec: row?.spec || '', qty: row?.qty || '', unit_price: row?.unit_price || '',
    supply_amount: row?.supply_amount || '', tax_amount: row?.tax_amount || '', total_amount: row?.total_amount || '',
    expected_date: row?.expected_date || '', account_id: row?.account_id || '', note: row?.note || '',
    brand_id: row?.brand_id ?? defaultBrandId ?? '',
    // 2026-10 요청(원가분석 탭) — 원가분석 목록에 필요한 5개 항목을 발주서 등록시점에 입력받는다.
    category: row?.category || '', project_name: row?.project_name || '', item_code: row?.item_code || '',
    unit: row?.unit || '', payment_terms: row?.payment_terms || '',
    // 2026-10 요청(상세내역 등록 개편) — 구분='소재'일 때만 쓰이는 재질/총중량.
    material: row?.material || '', total_weight: row?.total_weight || '',
  });
  const [partnerCompanies, setPartnerCompanies] = useState([]);
  useEffect(() => {
    API.get('/companies/customers-lookup', { params: { category: '협력사' } })
      .then(r => setPartnerCompanies(r.data || [])).catch(() => {});
  }, []);
  // 2026-10 요청(상세내역 등록 재개편) — 구분(사내/외주/사내+외주)과 별개로, 품목명이 '자재'일 때만
  // 재질/총중량/단가(kg) 자동계산 로직이 적용된다.
  const isMaterial = form.item_name === '자재';
  // 2026-10 요청: 수량×단가 입력시 공급가액 자동계산 (자재는 단가×총중량 방식을 따로 사용하므로 제외).
  useEffect(() => {
    if (isMaterial) return;
    const q = Number(onlyDigits(String(form.qty || '')));
    const p = Number(onlyDigits(String(form.unit_price || '')));
    if (q > 0 && p > 0) {
      const supply = String(q * p);
      if (supply !== String(form.supply_amount || '')) {
        setForm(f => ({ ...f, supply_amount: supply }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.qty, form.unit_price, form.item_name]);
  // 2026-10 요청(자재 전용) — 규격/재질/수량으로 총중량(kg) 자동계산 + 공급가액 = 단가×총중량(10원 올림).
  useEffect(() => {
    if (!isMaterial) return;
    const w = calcTotalWeightKg(form.spec, form.material, form.qty);
    if (w != null && String(w) !== String(form.total_weight || '')) {
      setForm(f => ({ ...f, total_weight: w }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.item_name, form.spec, form.material, form.qty]);
  useEffect(() => {
    if (!isMaterial) return;
    const p = Number(onlyDigits(String(form.unit_price || '')));
    const w = Number(form.total_weight || 0);
    if (p > 0 && w > 0) {
      const supply = String(Math.ceil((p * w) / 10) * 10);
      if (supply !== String(form.supply_amount || '')) {
        setForm(f => ({ ...f, supply_amount: supply }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.item_name, form.unit_price, form.total_weight]);
  useEffect(() => {
    const supply = Number(onlyDigits(String(form.supply_amount || '')));
    if (supply > 0) {
      const tax = String(Math.round(supply * 0.1));
      if (tax !== String(form.tax_amount || '')) {
        setForm(f => ({ ...f, tax_amount: tax }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.supply_amount]);
  // 2026-10 요청 — 공급가액/세액이 정해지면 합계도 실시간으로 자동계산한다.
  useEffect(() => {
    const supply = Number(onlyDigits(String(form.supply_amount || '')));
    const tax = Number(onlyDigits(String(form.tax_amount || '')));
    if (supply > 0) {
      const total = String(supply + tax);
      if (total !== String(form.total_amount || '')) {
        setForm(f => ({ ...f, total_amount: total }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.supply_amount, form.tax_amount]);
  const prefix = titlePrefix || '발주서';
  const submit = () => onSave(form);
  return (
    <Modal title={mode === 'add' ? `${prefix} 등록` : `${prefix} 수정`} width={560} onClose={onClose}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="발주일"><input type="date" style={formInput} value={form.po_date} onChange={e => setForm({ ...form, po_date: e.target.value })} /></Field>
        <Field label="입고예정일"><input type="date" style={formInput} value={form.expected_date} onChange={e => setForm({ ...form, expected_date: e.target.value })} /></Field>
        <Field label="협력사">
          <select style={formInput} value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
            <option value="">직접입력</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name}</option>)}
          </select>
        </Field>
        {!form.partner_id && (
          <Field label="협력사 직접입력">
            <input
              style={formInput}
              list="po-partner-company-list"
              placeholder="업체명 (업체관리 등록 협력사 검색)"
              value={form.partner_name_manual}
              onChange={e => setForm({ ...form, partner_name_manual: e.target.value })}
            />
            <datalist id="po-partner-company-list">
              {(partnerCompanies || []).map(c => <option key={c.id} value={c.company_name} />)}
            </datalist>
          </Field>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="구분">
          <select style={formInput} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            <option value="">선택안함</option>
            {PO_CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="프로젝트명"><input style={formInput} value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} /></Field>
        <Field label="품목명">
          <input
            style={formInput}
            list="po-item-name-list"
            placeholder="목록에서 선택 또는 직접입력"
            value={form.item_name}
            onChange={e => {
              const v = e.target.value;
              setForm(f => (v === '자재' ? { ...f, item_name: v } : { ...f, item_name: v, material: '', total_weight: '' }));
            }}
          />
          <datalist id="po-item-name-list">
            {ITEM_NAME_OPTIONS.map(v => <option key={v} value={v} />)}
          </datalist>
        </Field>
        {isMaterial && (
          <Field label="재질">
            <select style={formInput} value={form.material} onChange={e => setForm({ ...form, material: e.target.value })}>
              <option value="">선택</option>
              {MATERIAL_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: '0 0 30%' }}><Field label="규격"><input style={formInput} value={form.spec} onChange={e => setForm({ ...form, spec: e.target.value })} placeholder={isMaterial ? '평판: 가로*세로*높이(mm) / 환봉: Φ지름*길이(mm)' : ''} /></Field></div>
        <div style={{ flex: '0 0 20%' }}><Field label="단위"><input style={formInput} value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} /></Field></div>
        <div style={{ flex: '0 0 20%' }}><Field label="수량"><input style={formInput} value={form.qty} onChange={e => setForm({ ...form, qty: onlyDigits(e.target.value) })} /></Field></div>
        <div style={{ flex: '0 0 30%' }}><Field label={isMaterial ? '단가/kg' : '단가'}><input style={formInput} value={moneyFmt(form.unit_price)} onChange={e => setForm({ ...form, unit_price: onlyDigits(e.target.value) })} /></Field></div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {isMaterial && (
          <Field label="총중량(kg, 자동계산)"><input style={formInput} value={form.total_weight || ''} onChange={e => setForm({ ...form, total_weight: e.target.value.replace(/[^0-9.]/g, '') })} /></Field>
        )}
        <Field label={isMaterial ? '공급가액(단가×총중량, 10원 올림)' : '공급가액'}><input style={formInput} value={moneyFmt(form.supply_amount)} onChange={e => setForm({ ...form, supply_amount: onlyDigits(e.target.value) })} /></Field>
        <Field label="세액(공급가액 10% 자동계산)"><input style={formInput} value={moneyFmt(form.tax_amount)} onChange={e => setForm({ ...form, tax_amount: onlyDigits(e.target.value) })} /></Field>
        <Field label="합계(자동계산)"><input style={formInput} value={moneyFmt(form.total_amount)} onChange={e => setForm({ ...form, total_amount: onlyDigits(e.target.value) })} /></Field>
      </div>
      <Field label="매입 계정과목(선택)">
        <select style={formInput} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })}>
          <option value="">선택안함</option>
          {accounts.filter(a => a.account_type === '비용').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <BrandField brands={brands} value={form.brand_id} onChange={v => setForm({ ...form, brand_id: v })} />
      <Field label="비고"><textarea style={{ ...formInput, minHeight: 50 }} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={submit}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 원가분석 — 2026-10 요청. 발주서(acct_purchase_orders)를 기준으로 품목별 원가 내역을
// 구분/프로젝트명/품목코드/품목명/규격·사양/단위/수량/단가/공급가액/부가세/합계/결재조건/
// 결재여부/발주처/발주일/입고일 순서로 나열한다. 결재여부는 백엔드에서 발주서가 입고완료로
// 전환되며 자동생성된 acct_purchases 행의 payment_status를 JOIN해서 내려준다.
// ══════════════════════════════════════════════════════════════
// 2026-10 요청 — 결재조건: 분할결재 / 일시결재(선) / 일시결재(후) 중 선택.
const PAYMENT_TERMS_OPTIONS = ['분할결재', '일시결재(선)', '일시결재(후)'];

function CostAnalysisRow({ r, selectable, checked, onToggle, onPaidAmountSaved, partners, accounts, brands, canManage }) {
  // 2026-10 요청 — 이미 등록된 상세내역(발주서) 행도 수정할 수 있어야 한다. PoModal을
  // mode='edit'으로 재사용해 품목/수량/단가 등 전체 항목을 수정한다.
  const [editOpen, setEditOpen] = useState(false);
  const savePoEdit = (form) => {
    API.put(`/accounting/purchase-orders/${r.id}`, form)
      .then(() => { setEditOpen(false); onPaidAmountSaved && onPaidAmountSaved(); })
      .catch(e => alert(e?.response?.data?.message || '수정에 실패했습니다.'));
  };
  // 2026-10 요청 — 결재여부 셀을 클릭하면 모달창을 통해 결재금액(실제 결제한 금액)을 입력할 수 있도록 하고,
  // 입력된 결재금액은 지출(프로젝트 목록의 지출금액) 집계에 total_amount 대신 사용된다
  // (백엔드 PROJECT_SELECT/paid-amount 라우트 참고).
  // 입력방식은 금액 직접입력 또는 합계금액 기준 비율(%) 입력 중 선택 가능.
  const [modalOpen, setModalOpen] = useState(false);
  // 2026-10 요청 — 결재조건 셀 클릭시 분할결재/일시결재(선)/일시결재(후) 중 하나로 설정.
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsValue, setTermsValue] = useState(r.payment_terms || '');
  const openTermsModal = () => {
    setTermsValue(r.payment_terms || '');
    setTermsModalOpen(true);
  };
  const saveTerms = () => {
    if (!termsValue) { alert('결재조건을 선택해주세요.'); return; }
    API.patch(`/accounting/cost-analysis/${r.id}/payment-terms`, { payment_terms: termsValue })
      .then(() => { setTermsModalOpen(false); onPaidAmountSaved && onPaidAmountSaved(); })
      .catch(e => alert(e?.response?.data?.message || '결재조건 설정에 실패했습니다.'));
  };
  const [inputMode, setInputMode] = useState('amount'); // 'amount' | 'ratio'
  const [amountInput, setAmountInput] = useState('');
  const [ratioInput, setRatioInput] = useState('');
  const openModal = () => {
    setInputMode('amount');
    setAmountInput(String(r.paid_amount || r.total_amount || ''));
    setRatioInput('');
    setModalOpen(true);
  };
  const totalAmount = Number(r.total_amount) || 0;
  // 2026-10 요청 — 일부 금액만 결재된 경우 "미지급" 대신 "분할지급"으로 표시.
  const paidAmountNum = Number(r.paid_amount) || 0;
  const displayPayStatus = paidAmountNum <= 0
    ? (r.pay_status || '-')
    : (paidAmountNum >= totalAmount && totalAmount > 0 ? '지급완료' : '분할지급');
  const computedAmount = inputMode === 'ratio'
    ? Math.round(totalAmount * (Number(ratioInput) || 0) / 100)
    : (Number(onlyDigits(amountInput)) || 0);
  const save = () => {
    API.patch(`/accounting/cost-analysis/${r.id}/paid-amount`, { paid_amount: computedAmount })
      .then(() => { setModalOpen(false); onPaidAmountSaved && onPaidAmountSaved(); })
      .catch(e => alert(e?.response?.data?.message || '결재금액 등록에 실패했습니다.'));
  };
  // 2026-10 요청(상세내역 관리버튼 UX) — 관리열의 "수정" 버튼은 해당 행을 클릭했을 때만 나타난다.
  const [rowSelected, setRowSelected] = useState(false);
  return (
    <>
    <tr onClick={() => setRowSelected(v => !v)} style={{ cursor: 'pointer', background: rowSelected ? '#f0fdf9' : undefined }}>
      {selectable && (
        <td style={td} onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!checked} onChange={() => onToggle(r.id)} /></td>
      )}
      <td style={td}>{r.category || '-'}</td>
      <td style={td}>{r.project_name || '-'}</td>
      <td style={td}>{r.item_name || '-'}</td>
      <td style={td}>{r.spec || '-'}</td>
      <td style={td}>{r.unit || '-'}</td>
      <td style={td}>{r.qty || '-'}</td>
      <td style={td}>{moneyFmt(r.unit_price)}</td>
      <td style={td}>{moneyFmt(r.supply_amount)}</td>
      <td style={td}>{moneyFmt(r.tax_amount)}</td>
      <td style={td}>{moneyFmt(r.total_amount)}</td>
      <td style={{ ...td, cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openTermsModal(); }} title="클릭하여 결재조건 설정">
        {r.payment_terms || '-'}
      </td>
      <td style={{ ...td, minWidth: 150 }}>
        <div style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openModal(); }} title="클릭하여 결재금액 입력">
          {/* 2026-10 요청 — 일부만 결재된 경우 "미지급"이 아니라 "분할지급"으로 표시 */}
          <span>{displayPayStatus}</span>
          {r.paid_amount > 0 && <span style={{ color: C.primary, marginLeft: 6, fontWeight: 600 }}>({moneyFmt(r.paid_amount)})</span>}
        </div>
      </td>
      <td style={td}>{r.partner_name || r.partner_name_manual || '-'}</td>
      <td style={td}>{r.po_date || '-'}</td>
      <td style={td}>{r.expected_date || '-'}</td>
      {canManage && (
        <td style={td} onClick={e => e.stopPropagation()}>
          {rowSelected && <button style={slimAddBtn} onClick={() => setEditOpen(true)}>수정</button>}
        </td>
      )}
    </tr>
    {modalOpen && (
        <Modal title="결재금액 입력" width={420} onClose={() => setModalOpen(false)}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
            합계금액: <strong>{moneyFmt(r.total_amount)}</strong>원
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
              <input type="radio" checked={inputMode === 'amount'} onChange={() => setInputMode('amount')} />
              금액으로 입력
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
              <input type="radio" checked={inputMode === 'ratio'} onChange={() => setInputMode('ratio')} />
              비율(%)로 입력
            </label>
          </div>
          {inputMode === 'amount' ? (
            <Field label="결재금액">
              <input
                autoFocus
                style={formInput}
                value={moneyFmt(amountInput)}
                onChange={e => setAmountInput(onlyDigits(e.target.value))}
                onKeyDown={e => { if (e.key === 'Enter') save(); }}
              />
            </Field>
          ) : (
            <Field label="비율 (%)">
              <input
                autoFocus
                type="number"
                step="0.1"
                style={formInput}
                value={ratioInput}
                onChange={e => setRatioInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') save(); }}
              />
            </Field>
          )}
          <div style={{ fontSize: 13, color: '#666', margin: '4px 0 18px' }}>
            결재금액: <strong style={{ color: C.primary }}>{moneyFmt(computedAmount)}</strong>원
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button style={slimDelBtn} onClick={() => setModalOpen(false)}>취소</button>
            <button style={slimAddBtn} onClick={save}>저장</button>
          </div>
        </Modal>
      )}
    {termsModalOpen && (
        <Modal title="결재조건 설정" width={420} onClose={() => setTermsModalOpen(false)}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
            {PAYMENT_TERMS_OPTIONS.map(v => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input type="radio" checked={termsValue === v} onChange={() => setTermsValue(v)} />
                {v}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button style={slimDelBtn} onClick={() => setTermsModalOpen(false)}>취소</button>
            <button style={slimAddBtn} onClick={saveTerms}>저장</button>
          </div>
        </Modal>
      )}
    {editOpen && (
        <PoModal
          mode="edit"
          titlePrefix="상세내역"
          row={r}
          partners={partners}
          accounts={accounts}
          brands={brands}
          defaultBrandId={r.brand_id || ''}
          onClose={() => setEditOpen(false)}
          onSave={savePoEdit}
        />
      )}
    </>
  );
}

// 2026-10 요청 — 추가/삭제 버튼: 기존 btn()/smallBtn()보다 높이 20% 축소 + 배경색 제거(테두리만).
const slimAddBtn = { background: 'transparent', color: C.primary, border: `1px solid ${C.primary}`, borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const slimDelBtn = { background: 'transparent', color: '#DC2626', border: '1px solid #DC2626', borderRadius: 6, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };

// 2026-10 요청 — 원가분석 "인쇄" 버튼: 선택된 프로젝트의 상세내역을 가로(A4 landscape)로 인쇄한다.
// 다른 메뉴(Assets.js 등)의 공용 인쇄와 같은 방식(새 창 + 출력일시/출력자 하단 표기).
// summary(선택) — 종합분석 화면에서 누른 경우 { cards, groups, groupLabel, filterLabel }:
// 요약 카드/구분별 사용비용 표를 먼저 찍고, 구분을 선택해 둔 경우 그 아래 목록(rows)을 이어서 찍는다.
function printCostDetail(projectName, rows, summary, info) {
  let user = {};
  try { user = JSON.parse(sessionStorage.getItem('user') || '{}'); } catch (e) { user = {}; }
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const num = (v) => (v === null || v === undefined || v === '' ? '-' : moneyFmt(v));
  const payStatus = (r) => {
    const pa = Number(r.paid_amount) || 0;
    const ta = Number(r.total_amount) || 0;
    const st = pa <= 0 ? (r.pay_status || '-') : (pa >= ta && ta > 0 ? '지급완료' : '분할지급');
    return pa > 0 ? `${st} (${moneyFmt(pa)})` : st;
  };
  const cols = [
    ['구분', r => r.category || '-'], ['프로젝트명', r => r.project_name || '-'], ['품목명', r => r.item_name || '-'],
    ['규격/사양', r => r.spec || '-'], ['단위', r => r.unit || '-'], ['수량', r => r.qty || '-'],
    ['단가', r => num(r.unit_price)], ['공급가액', r => num(r.supply_amount)], ['부가세', r => num(r.tax_amount)],
    ['합계', r => num(r.total_amount)], ['결재조건', r => r.payment_terms || '-'], ['결재여부', payStatus],
    ['발주처', r => r.partner_name || r.partner_name_manual || '-'], ['발주일', r => r.po_date || '-'], ['입고예정일', r => r.expected_date || '-'],
  ];
  const tableHtml = (rs) => {
    const sum = (k) => rs.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    const thead = `<tr>${cols.map(c => `<th>${c[0]}</th>`).join('')}</tr>`;
    const tbody = rs.map(r => `<tr>${cols.map(c => `<td>${esc(c[1](r))}</td>`).join('')}</tr>`).join('') ||
      `<tr><td colspan="${cols.length}" style="padding:20px;color:#999;">데이터가 없습니다.</td></tr>`;
    const tfoot = rs.length
      ? `<tr style="background:#f5f5f5;font-weight:700;"><td colspan="7">합계</td><td>${moneyFmt(sum('supply_amount'))}</td><td>${moneyFmt(sum('tax_amount'))}</td><td>${moneyFmt(sum('total_amount'))}</td><td colspan="5"></td></tr>`
      : '';
    return `<table class="dt"><thead>${thead}</thead><tbody>${tbody}${tfoot}</tbody></table>`;
  };
  const now = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  const printDT = `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())} ${p2(now.getHours())}:${p2(now.getMinutes())}`;
  const title = `${projectName} ${summary ? '종합분석' : '상세내역'}`;
  // 화면(UI)과 동일한 카드/타일 형태로 구성
  const cardBox = (c) => `<div class="card"><div class="cl">${esc(c.label)}</div>
    <div class="cv" style="color:${c.color || '#222'}">${esc(c.value)}${c.suffix ? `<span class="cs">${esc(c.suffix)}</span>` : ''}</div></div>`;
  const tileBox = (t, withPaidText) => `<div class="tile${t.on ? ' on' : ''}">
    <div class="tt"><span class="tn">${esc(t.label)}</span><span>${t.count}건</span></div>
    <div class="ta">${moneyFmt(t.amt)}</div>
    <div class="bar"><div style="width:${t.pct}%"></div></div>
    ${withPaidText ? `<div class="tp">지급 ${moneyFmt(t.paid)} · 미지급 ${moneyFmt(t.unpaid)}</div>` : ''}</div>`;
  const pages = [];
  if (summary) {
    const cardsHtml = `<div class="cards">${summary.cards.map(cardBox).join('')}</div>`;
    const groupsHtml = `<div class="sec"><b>구분별 사용비용</b><span class="hint">공급가액 기준</span></div>
      <div class="grid" style="grid-template-columns:repeat(${Math.max(1, Math.min(summary.groups.length, 4))},minmax(0,1fr));">${summary.groups.map(g => tileBox(g, true)).join('')}</div>`;
    // 첫 장은 카드 + 구분별 종합만, 구분별 상세는 각각 새 페이지
    // 첫 장: 구분별 종합 아래에 구분별(사내/용역/외주) 품목 카드만 차례로 추가
    const itemCardsHtml = (summary.sections || []).map(sec => `
      <div class="sec" style="margin-top:14px;"><b>${esc(sec.label)}</b><span class="hint">품목별 · 공급가액 기준</span></div>
      <div class="grid" style="grid-template-columns:repeat(7,minmax(0,1fr));">${sec.items.map(t => tileBox(t, false)).join('')}</div>`).join('');
    // 2페이지부터: 구분별 상세 목록(각각 새 페이지)
    pages.push(cardsHtml + groupsHtml + itemCardsHtml);
    (summary.sections || []).forEach(sec => pages.push(`
      <div class="sec"><b>${esc(sec.label)} 상세비용</b><span class="hint">공급가액 기준</span></div>
      ${tableHtml(sec.rows)}`));
  } else {
    const infoHtml = info ? `<div class="info">${info.map(i => `<div><span style="color:#888">${esc(i[0])}</span> <b style="${i[2] ? `color:${i[2]}` : ''}">${esc(i[1])}</b></div>`).join('')}</div>` : '';
    pages.push(infoHtml + tableHtml(rows));
  }
  // 모든 페이지 동일한 상/하단 여백 + 하단 출력일시/출력자(고정) 반복
  const wrapped = pages.map((p, i) => `<table class="wrap${i > 0 ? ' brk' : ''}"><thead><tr><td><div style="height:10mm"></div></td></tr></thead>
    <tbody><tr><td>${i === 0 ? `<h2 style="text-align:center;margin:0 0 12px;">${esc(title)}</h2>` : ''}${p}</td></tr></tbody>
    <tfoot><tr><td><div style="height:16mm"></div></td></tr></tfoot></table>`).join('');
  const pw = window.open('', '_blank', 'width=1200,height=900');
  if (!pw) { alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.'); return; }
  pw.document.write(`<!DOCTYPE html><html><head><title>${esc(title)}</title>
    <style>@page{size:A4 landscape;margin:0}body{font-family:sans-serif;margin:0;padding:0 10mm;box-sizing:border-box}
    *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    table.wrap{width:100%;border-collapse:collapse;margin:0}
    table.wrap>thead>tr>td,table.wrap>tbody>tr>td,table.wrap>tfoot>tr>td{border:none;padding:0;text-align:left;vertical-align:top}
    table.wrap.brk{page-break-before:always}
    table.dt{border-collapse:collapse;width:100%;font-size:11px;margin-top:12px}
    .dt th{padding:7px 6px;font-size:11px;font-weight:600;color:#666;background:#f8fafc;text-align:center;white-space:nowrap;border-bottom:1px solid #e5e7eb}
    .dt td{padding:6px 6px;color:#333;border-top:1px solid #f0f0f0;text-align:center}
    .dt thead{display:table-header-group}.dt tr{page-break-inside:avoid}
    .ft{position:fixed;left:10mm;right:10mm;bottom:7mm;display:flex;justify-content:space-between;padding-top:6px;border-top:1px solid #ddd;font-size:11px;color:#555;background:#fff}
    .cards{display:flex;gap:10px;margin-bottom:16px}
    .card{flex:1 1 0;padding:12px 14px;border:1px solid #e5e7eb;border-radius:10px}
    .cl{font-size:11px;color:#888;margin-bottom:6px}.cv{font-size:17px;font-weight:700}.cs{font-size:11px;font-weight:500;margin-left:5px}
    .sec{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:8px;font-size:13px}.hint{font-size:11px;color:#999}
    .grid{display:grid;gap:8px}
    .pg{page-break-before:always;padding-top:4px}
    .tile{padding:10px 12px;border:0.5px solid #e5e7eb;border-radius:12px;min-width:0}
    .tile.on{padding:9.5px 11.5px;border:1.5px solid #222}
    .tt{display:flex;justify-content:space-between;font-size:11px;color:#777}.tile.on .tt{color:#222}
    .tn{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .ta{font-size:16px;font-weight:600;margin:3px 0 7px}
    .bar{height:4px;border-radius:2px;background:#e5e7eb;overflow:hidden}.bar div{height:100%;background:#6b7280}
    .tp{font-size:10px;color:#999;margin-top:5px}
    .info{display:flex;gap:20px;flex-wrap:wrap;padding:8px 12px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;font-size:12px}
    </style></head><body>
    ${wrapped}
    <div class="ft"><span>출력일시: ${printDT}</span><span>출력자: ${esc(printedByLabel(user))}</span></div>
    </body></html>`);
  pw.document.close(); pw.focus();
  logPrint('accounting', title);
  setTimeout(() => { pw.print(); pw.close(); }, 500);
}

function CostAnalysisTab({ partners, accounts, brands, canManage, canDelete, headerSlot }) {
  // 2026-10 요청 — "원가분석 탭은 회계관리의 다른 탭과 무관하게 독립적으로 구성": 상단 헤더의
  // 브랜드 선택 버튼(브랜드1/브랜드2/공통관리/전체보기)에 이 탭의 데이터가 연동되지 않도록
  // brand_id 쿼리파라미터를 더 이상 넘기지 않는다 — 어떤 브랜드가 선택돼 있어도 등록된 프로젝트
  // 전체를 그대로 보여준다(브랜드 자체는 프로젝트/상세내역 등록 폼에서 선택사항으로만 남긴다).
  //
  // 2026-10 요청(목록 = 프로젝트 목록) — 상단 목록은 등록된 프로젝트(입력값) 기준으로 구성하고,
  // 발주내역(acct_purchase_orders)이 아닌 acct_projects 등록 데이터를 그대로 보여준다.
  // 목록에서 프로젝트를 선택하면 그 프로젝트의 발주 상세내역을 하단에 표시한다.
  const [projects, setProjects] = useState([]);
  const loadProjects = () => API.get('/accounting/projects').then(r => setProjects(r.data)).catch(() => {});
  useEffect(() => { loadProjects(); }, []); // eslint-disable-line
  const { sorted, sortKey, sortDir, toggleSort } = useSort(projects, 'id');

  const [projectModal, setProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState(null);
  const selectedProjectInfo = projects.find(p => p.project_name === selectedProjectName);

  const openEditProject = (p) => { setEditingProject(p); setProjectModal(true); };
  const handleDeleteProject = (p) => {
    if (!window.confirm(`'${p.project_name}' 프로젝트를 삭제하시겠습니까?`)) return;
    API.delete(`/accounting/projects/${p.id}`)
      .then(() => {
        if (selectedProjectName === p.project_name) setSelectedProjectName(null);
        loadProjects();
      })
      .catch(e => alert(e?.response?.data?.message || '삭제에 실패했습니다.'));
  };

  const [detailRows, setDetailRows] = useState([]);
  const loadDetail = () => {
    if (!selectedProjectName) { setDetailRows([]); return; }
    API.get('/accounting/cost-analysis', { params: { project_name: selectedProjectName } })
      .then(r => setDetailRows(r.data)).catch(() => {});
  };
  useEffect(() => { loadDetail(); setSelectedIds([]); setDetailActionsOpen(false); setDetailView('detail'); setCostGroupOpen(null); setItemFilter(null); }, [selectedProjectName]); // eslint-disable-line
  const detailSort = useSort(detailRows, 'po_date');

  // 2026-10 요청(상세내역 제목 클릭 시 등록/삭제 버튼 노출)
  const [detailActionsOpen, setDetailActionsOpen] = useState(false);
  // 2026-10 요청(종합분석/상세내역 버튼) — 'detail'(상세내역) | 'summary'(종합분석). 종합분석에서 클릭한 비용 카드 key.
  const [detailView, setDetailView] = useState('detail');
  const [costGroupOpen, setCostGroupOpen] = useState(null);
  const [itemFilter, setItemFilter] = useState(null); // 종합분석 품목 칩 필터(null=전체)
  // 추가/삭제 버튼. 추가: 발주서등록 모달(프로젝트명 고정)로 등록. 삭제: 체크한 행을 일괄 삭제.
  const [poModal, setPoModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const toggleRow = (id) => setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const savePo = (form) => {
    API.post('/accounting/purchase-orders', form)
      .then(() => { setPoModal(false); loadDetail(); loadProjects(); })
      .catch(e => alert(e?.response?.data?.message || '등록에 실패했습니다.'));
  };
  const removeSelected = () => {
    if (selectedIds.length === 0) { alert('삭제할 항목을 선택해 주세요.'); return; }
    if (!window.confirm(`선택한 ${selectedIds.length}건을 삭제하시겠습니까?`)) return;
    Promise.all(selectedIds.map(id => API.delete(`/accounting/purchase-orders/${id}`)))
      .then(() => { setSelectedIds([]); loadDetail(); loadProjects(); })
      .catch(e => alert(e?.response?.data?.message || '삭제에 실패했습니다.'));
  };

  // 2026-10 요청(종합분석) — 공급가액 기준 집계 (클라이언트 계산)
  // paid_amount는 합계(부가세 포함) 기준으로 입력되므로, 공급가액 기준 지급액으로 환산해 모든 집계를 통일한다.
  const rowPaid = (r) => {
    const pa = Number(r.paid_amount) || 0;
    const ta = Number(r.total_amount) || 0;
    const sa = Number(r.supply_amount) || 0;
    if (pa <= 0) return 0;
    if (r.category === '용역') return pa; // 용역: 결재된 금액을 그대로 지급으로 적용
    if (ta > 0) return Math.min(sa, Math.round(pa * sa / ta));
    return Math.min(sa, pa);
  };
  const sumPaid = (rows) => rows.reduce((s, r) => s + rowPaid(r), 0);
  const paidSum = sumPaid(detailRows);
  const totalSum = detailRows.reduce((s, r) => s + (Number(r.supply_amount) || 0), 0);
  const unpaidSum = totalSum - paidSum;
  const progressCostSum = paidSum + unpaidSum;
  const contractAmt = Number(selectedProjectInfo?.contract_amount) || 0;
  const marginAmt = Number(selectedProjectInfo?.margin_amount) || 0;
  const marginPct = contractAmt ? (marginAmt / contractAmt * 100).toFixed(1) : '0.0';
  const knownCats = COST_GROUPS.map(g => g.match);
  const costGroups = [
    ...COST_GROUPS.map(g => ({ ...g, rows: detailRows.filter(r => g.match(r.category)) })),
  ];
  const otherRows = detailRows.filter(r => !knownCats.some(m => m(r.category)));
  if (otherRows.length > 0) costGroups.push({ key: 'etc', label: '기타(사내+외주/미구분)', color: '#6B7280', rows: otherRows });
  const groupAmount = (rows) => rows.reduce((s, r) => s + (Number(r.supply_amount) || 0), 0);
  const openGroup = costGroups.find(g => g.key === costGroupOpen) || null;
  const viewBtn = (active) => ({
    padding: '6px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
    border: `1px solid ${active ? C.primary : C.border}`,
    background: active ? C.primary : '#fff', color: active ? '#fff' : '#555',
  });
  // 인쇄 버튼 — 현재 선택된 화면(상세내역/종합분석) 기준으로 아래 내용을 가로로 인쇄
  const handlePrint = () => {
    if (detailView !== 'summary') {
      const marginColor = marginAmt >= 0 ? C.primary : '#DC2626';
      printCostDetail(selectedProjectName, detailSort.sorted || detailRows, null, [
        ['프로젝트명', selectedProjectName], ['고객사', selectedProjectInfo?.customer_name || '-'], ['계약', moneyFmt(contractAmt)],
        ['지급완료', moneyFmt(paidSum)], ['미지급', moneyFmt(unpaidSum)], ['진행비용 합계', moneyFmt(progressCostSum)],
        ['마진', `${moneyFmt(marginAmt)} (${marginPct}%)`, marginColor],
      ]);
      return;
    }
    const tile = (label, rows, on) => {
      const amt = groupAmount(rows);
      const paid = sumPaid(rows);
      return { label, count: rows.length, amt, paid, unpaid: amt - paid, on,
        pct: amt > 0 ? Math.max(0, Math.min(100, paid / amt * 100)) : 0 };
    };
    // 2페이지 이후: 구분(사내/용역/외주/기타)별로 페이지를 나눠 상세비용 인쇄
    const sections = costGroups.filter(g => g.rows.length > 0).map(g => {
      const map = {};
      g.rows.forEach(r => { const k = r.item_name || '(미지정)'; (map[k] = map[k] || []).push(r); });
      return {
        label: g.label.replace('비용', ''),
        items: [tile('합계', g.rows, true),
          ...Object.entries(map).sort((a, b) => groupAmount(b[1]) - groupAmount(a[1])).map(([n, rs]) => tile(n, rs, false))],
        rows: g.rows,
      };
    });
    printCostDetail(selectedProjectName, [], {
      cards: [
        { label: '계약금액', value: moneyFmt(contractAmt) },
        { label: '지급금액', value: moneyFmt(paidSum) },
        { label: '미지급금액', value: moneyFmt(unpaidSum), color: unpaidSum > 0 ? '#EA580C' : undefined },
        { label: '진행비용합계', value: moneyFmt(progressCostSum) },
        { label: '마진', value: moneyFmt(marginAmt), color: marginAmt >= 0 ? C.primary : '#DC2626', suffix: `(마진률 : ${marginPct}%)` },
      ],
      groups: costGroups.map(g => tile(g.label.replace('비용', ''), g.rows, false)),
      sections,
    });
  };
  const summaryCard = (label, value, color, suffix) => (
    <div style={{ flex: '1 1 180px', minWidth: 170, padding: '16px 18px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10 }}>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#222' }}>
        {moneyFmt(value)}
        {suffix && <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 6 }}>{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Section headerSlot={headerSlot} action={
        canManage && <button style={btn(C.primary)} onClick={() => { setEditingProject(null); setProjectModal(true); }}>+ 프로젝트 등록</button>
      }>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1400 }}>
            <thead>
              <tr>
                <th style={th}>구분</th>
                <th style={th}>계약체결일</th>
                <th style={th}>시작일</th>
                <th style={th}>종료일</th>
                <th style={th}><Sortable label="프로젝트명" sortKey="project_name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
                <th style={th}>고객사</th>
                <th style={th}>견적금액</th>
                <th style={th}>계약금액</th>
                <th style={th}>지출금액</th>
                <th style={th}>마진</th>
                <th style={th}>진행구분</th>
                <th style={th}>비고</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && <tr><td colSpan={13} style={{ ...td, color: '#aaa', padding: 20 }}>등록된 프로젝트가 없습니다.</td></tr>}
              {sorted.map(p => (
                <tr key={p.id} onClick={() => setSelectedProjectName(prev => prev === p.project_name ? null : p.project_name)}
                  style={{ cursor: 'pointer', background: selectedProjectName === p.project_name ? '#f0fdf9' : undefined }}>
                  <td style={td}>{p.category || '-'}</td>
                  <td style={td}>{p.contract_date || '-'}</td>
                  <td style={td}>{p.start_date || '-'}</td>
                  <td style={td}>{p.end_date || '-'}</td>
                  <td style={td}>{p.project_name}</td>
                  <td style={td}>{p.customer_name || '-'}</td>
                  <td style={td}>{moneyFmt(p.quote_amount)}</td>
                  <td style={td}>{moneyFmt(p.contract_amount)}</td>
                  <td style={td}>{moneyFmt(p.spend_amount)}</td>
                  <td style={{ ...td, color: (p.margin_amount || 0) >= 0 ? C.primary : '#DC2626', fontWeight: 700 }}>{moneyFmt(p.margin_amount)}</td>
                  <td style={td}>{p.progress_type || '-'}</td>
                  <td style={td}>{p.note || '-'}</td>
                  <td style={td} onClick={e => e.stopPropagation()}>
                    {/* 2026-10 요청(프로젝트 목록 관리버튼 UX) — 행을 클릭했을 때만 수정/삭제 버튼 노출 */}
                    {selectedProjectName === p.project_name && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {canManage && <button style={slimAddBtn} onClick={() => openEditProject(p)}>수정</button>}
                        {canDelete && <button style={slimDelBtn} onClick={() => handleDeleteProject(p)}>삭제</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {selectedProjectName && (
        <Section>
          {/* 2026-10 요청(상세내역 제목 클릭 시 등록/삭제 버튼 노출) — 제목 텍스트를 클릭하면 추가/삭제 버튼이 나타남 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div
              style={{ fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: 8 }}
              onClick={() => setDetailActionsOpen(v => !v)}
            >
              <span>{`${selectedProjectName} 상세내역`}</span>
              <span style={{ fontSize: 12, fontWeight: 400, color: '#999' }}>(매입/지출관리, 업무관리 항목에서 집계하여 자동등록)</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {detailView === 'detail' && detailActionsOpen && (
                <>
                  {canManage && <button style={slimAddBtn} onClick={() => setPoModal(true)}>추가</button>}
                  {canDelete && <button style={slimDelBtn} onClick={removeSelected}>삭제</button>}
                  <span style={{ width: 8 }} />
                </>
              )}
              <button style={viewBtn(false)} onClick={handlePrint}>인쇄</button>
              <button style={viewBtn(detailView === 'summary')} onClick={() => setDetailView('summary')}>종합분석</button>
              <button style={viewBtn(detailView === 'detail')} onClick={() => setDetailView('detail')}>상세내역</button>
            </div>
          </div>
          {detailView === 'summary' && (
            <div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                {summaryCard('계약금액', contractAmt)}
                {summaryCard('지급금액', paidSum)}
                {summaryCard('미지급금액', unpaidSum, unpaidSum > 0 ? '#EA580C' : undefined)}
                {summaryCard('진행비용합계', progressCostSum)}
                {summaryCard('마진', marginAmt, marginAmt >= 0 ? C.primary : '#DC2626', `(마진률 : ${marginPct}%)`)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>구분별 사용비용</span>
                <span style={{ fontSize: 12, color: '#999' }}>공급가액 기준 · 카드를 클릭하면 아래에 목록이 표시됩니다</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(costGroups.length, 4)}, minmax(0, 1fr))`, gap: 10 }}>
                {costGroups.map(g => {
                  const amt = groupAmount(g.rows);
                  const gPaid = sumPaid(g.rows);
                  const gUnpaid = amt - gPaid;
                  const active = costGroupOpen === g.key;
                  const paidPct = amt > 0 ? Math.max(0, Math.min(100, gPaid / amt * 100)) : 0;
                  return (
                    <div key={g.key}
                      onClick={() => { setCostGroupOpen(prev => prev === g.key ? null : g.key); setItemFilter(null); }}
                      style={{ cursor: 'pointer', padding: active ? '11.5px 13.5px' : '12px 14px', borderRadius: 12, background: '#fff',
                        border: active ? '1.5px solid #222' : `0.5px solid ${C.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: active ? '#222' : '#777' }}>
                        <span>{g.label.replace('비용', '')}</span><span>{g.rows.length}건</span>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, margin: '4px 0 8px' }}>{moneyFmt(amt)}</div>
                      <div style={{ height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
                        <div style={{ width: `${paidPct}%`, height: '100%', background: '#6b7280' }} />
                      </div>
                      <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>
                        지급 {moneyFmt(gPaid)} · 미지급 {moneyFmt(gUnpaid)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {openGroup && (() => {
                const map = {};
                openGroup.rows.forEach(r => {
                  const k = r.item_name || '(미지정)';
                  if (!map[k]) map[k] = { rows: [] };
                  map[k].rows.push(r);
                });
                const items = Object.entries(map)
                  .map(([name, v]) => [name, v.rows])
                  .sort((a, b) => groupAmount(b[1]) - groupAmount(a[1]));
                const shownRows = itemFilter ? openGroup.rows.filter(r => (r.item_name || '(미지정)') === itemFilter) : openGroup.rows;
                const itemTile = (label, rows, on, onClick) => {
                  const amt = groupAmount(rows);
                  const paid = sumPaid(rows);
                  const unpaid = amt - paid;
                  const pct = amt > 0 ? Math.max(0, Math.min(100, paid / amt * 100)) : 0;
                  return (
                    <div key={label} onClick={onClick}
                      style={{ cursor: 'pointer', padding: on ? '11.5px 13.5px' : '12px 14px', borderRadius: 12, background: '#fff',
                        border: on ? '1.5px solid #222' : `0.5px solid ${C.border}`, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: on ? '#222' : '#777' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                        <span style={{ flexShrink: 0, marginLeft: 6 }}>{rows.length}건</span>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 600, margin: '4px 0 8px' }}>{moneyFmt(amt)}</div>
                      <div style={{ height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#6b7280' }} />
                      </div>
                    </div>
                  );
                };
                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(items.length + 1, 7)}, minmax(0, 1fr))`, gap: 10, marginBottom: 10 }}>
                      {itemTile('합계', openGroup.rows, !itemFilter, () => setItemFilter(null))}
                      {items.map(([name, rows]) => itemTile(name, rows, itemFilter === name, () => setItemFilter(prev => prev === name ? null : name)))}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1400 }}>
                        <thead>
                          <tr>
                            <th style={th}>구분</th>
                            <th style={th}>프로젝트명</th>
                            <th style={th}>품목명</th>
                            <th style={th}>규격/사양</th>
                            <th style={th}>단위</th>
                            <th style={th}>수량</th>
                            <th style={th}>단가</th>
                            <th style={th}>공급가액</th>
                            <th style={th}>부가세</th>
                            <th style={th}>합계</th>
                            <th style={th}>결재조건</th>
                            <th style={th}>결재여부</th>
                            <th style={th}>발주처</th>
                            <th style={th}>발주일</th>
                            <th style={th}>입고예정일</th>
                            {canManage && <th style={th}>관리</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {shownRows.length === 0 && <tr><td colSpan={canManage ? 16 : 15} style={{ ...td, color: '#aaa', padding: 20 }}>해당 내역이 없습니다.</td></tr>}
                          {shownRows.map(r => (
                            <CostAnalysisRow key={r.id} r={r} onPaidAmountSaved={() => { loadDetail(); loadProjects(); }}
                              partners={partners} accounts={accounts} brands={brands} canManage={canManage} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {detailView === 'detail' && (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, fontSize: 13 }}>
            <div><span style={{ color: '#888' }}>프로젝트명</span> <b>{selectedProjectName}</b></div>
            <div><span style={{ color: '#888' }}>고객사</span> <b>{selectedProjectInfo?.customer_name || '-'}</b></div>
            <div><span style={{ color: '#888' }}>계약</span> <b>{moneyFmt(selectedProjectInfo?.contract_amount)}</b></div>
            {(() => {
              // 2026-10 요청 5 — "지출"을 "지급완료"로 변경, "미지급" 추가, "진행비용 합계"(=지급완료+미지급) 추가.
              // 2026-10 재요청 — 지급완료/미지급/진행비용 합계는 합계(세액포함)가 아닌 공급가액 기준으로 계산.
              return (
                <>
                  <div><span style={{ color: '#888' }}>지급완료</span> <b>{moneyFmt(paidSum)}</b></div>
                  <div><span style={{ color: '#888' }}>미지급</span> <b>{moneyFmt(unpaidSum)}</b></div>
                  <div><span style={{ color: '#888' }}>진행비용 합계</span> <b>{moneyFmt(paidSum + unpaidSum)}</b></div>
                </>
              );
            })()}
            <div style={{ color: (selectedProjectInfo?.margin_amount || 0) >= 0 ? C.primary : '#DC2626', fontWeight: 700 }}>
              마진 {moneyFmt(selectedProjectInfo?.margin_amount)}
              {' '}
              ({(() => {
                const contract = Number(selectedProjectInfo?.contract_amount) || 0;
                const margin = Number(selectedProjectInfo?.margin_amount) || 0;
                if (!contract) return '0.0%';
                return `${(margin / contract * 100).toFixed(1)}%`;
              })()})
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1400 }}>
              <thead>
                <tr>
                  <th style={th}></th>
                  <th style={th}>구분</th>
                  <th style={th}>프로젝트명</th>
                  <th style={th}>품목명</th>
                  <th style={th}>규격/사양</th>
                  <th style={th}>단위</th>
                  <th style={th}>수량</th>
                  <th style={th}>단가</th>
                  <th style={th}>공급가액</th>
                  <th style={th}>부가세</th>
                  <th style={th}>합계</th>
                  <th style={th}>결재조건</th>
                  <th style={th}>결재여부</th>
                  <th style={th}>발주처</th>
                  <th style={th}><Sortable label="발주일" sortKey="po_date" activeKey={detailSort.sortKey} dir={detailSort.sortDir} onClick={detailSort.toggleSort} /></th>
                  <th style={th}>입고예정일</th>
                  {canManage && <th style={th}>관리</th>}
                </tr>
              </thead>
              <tbody>
                {detailSort.sorted.length === 0 && <tr><td colSpan={canManage ? 17 : 16} style={{ ...td, color: '#aaa', padding: 20 }}>조회된 내역이 없습니다.</td></tr>}
                {detailSort.sorted.map(r => (
                  <CostAnalysisRow key={r.id} r={r} selectable checked={selectedIds.includes(r.id)} onToggle={toggleRow} onPaidAmountSaved={() => { loadDetail(); loadProjects(); }}
                    partners={partners} accounts={accounts} brands={brands} canManage={canManage} />
                ))}
              </tbody>
            </table>
          </div>
          </>)}
        </Section>
      )}

      {projectModal && (
        <ProjectModal
          defaultBrandId=""
          editRow={editingProject}
          onClose={() => { setProjectModal(false); setEditingProject(null); }}
          onSave={() => { loadProjects(); setProjectModal(false); setEditingProject(null); }}
        />
      )}

      {poModal && selectedProjectName && (
        <PoModal
          mode="add"
          titlePrefix="상세내역"
          row={{ project_name: selectedProjectName }}
          partners={partners}
          accounts={accounts}
          brands={brands}
          defaultBrandId=""
          onClose={() => setPoModal(false)}
          onSave={savePo}
        />
      )}
    </div>
  );
}

// 2026-10 요청(원가분석 탭 - 프로젝트 등록) — 업무현황 리스트에서 가져오기(tasks 검색) 또는
// 직접입력 중 하나로 프로젝트명을 채우고, 고객사/견적금액/계약금액을 입력받아 등록한다.
function ProjectModal({ defaultBrandId, editRow, onClose, onSave }) {
  const isEdit = !!editRow;
  const [mode, setMode] = useState('manual');
  const [taskCategories, setTaskCategories] = useState([]);
  const [taskCategory, setTaskCategory] = useState('');
  const [taskQuery, setTaskQuery] = useState('');
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form, setForm] = useState(editRow ? {
    project_name: editRow.project_name || '',
    customer_name: editRow.customer_name || '',
    quote_amount: editRow.quote_amount || '',
    contract_amount: editRow.contract_amount || '',
    category: editRow.category || '',
    contract_date: editRow.contract_date || '',
    start_date: editRow.start_date || '',
    end_date: editRow.end_date || '',
    progress_type: editRow.progress_type || '',
    note: editRow.note || '',
  } : {
    project_name: '', customer_name: '', quote_amount: '', contract_amount: '',
    category: '', contract_date: '', start_date: '', end_date: '', progress_type: '', note: '',
  });
  const [saving, setSaving] = useState(false);

  // 2026-10 요청 — 업무 검색 좌측에 업무구분(탭) 선택 칸 추가. task_categories를 그대로 노출.
  useEffect(() => {
    API.get('/accounting/task-categories').then(r => setTaskCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== 'task') return;
    const t = setTimeout(() => {
      API.get('/accounting/task-options', { params: { q: taskQuery, category: taskCategory } }).then(r => setTaskOptions(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [taskQuery, taskCategory, mode]);

  const pickTask = (t) => { setSelectedTask(t); setForm(f => ({ ...f, project_name: t.title })); };

  const submit = () => {
    if (!form.project_name) { alert('프로젝트명을 입력해 주세요.'); return; }
    setSaving(true);
    const payload = {
      project_name: form.project_name,
      customer_name: form.customer_name,
      quote_amount: form.quote_amount,
      contract_amount: form.contract_amount,
      category: form.category,
      contract_date: form.contract_date,
      start_date: form.start_date,
      end_date: form.end_date,
      progress_type: form.progress_type,
      note: form.note,
      source_type: mode === 'task' ? 'task' : 'manual',
      source_task_id: mode === 'task' ? (selectedTask?.id || null) : null,
      brand_id: defaultBrandId,
    };
    const req = isEdit
      ? API.put(`/accounting/projects/${editRow.id}`, payload)
      : API.post('/accounting/projects', payload);
    req.then(() => onSave()).catch(e => alert(e?.response?.data?.message || '저장에 실패했습니다.')).finally(() => setSaving(false));
  };

  return (
    <Modal title={isEdit ? '프로젝트 수정' : '프로젝트 등록'} width={480} onClose={onClose}>
      {!isEdit && (
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button style={mode === 'task' ? btn(C.primary) : outlineBtn} onClick={() => setMode('task')}>업무현황에서 가져오기</button>
        <button style={mode === 'manual' ? btn(C.primary) : outlineBtn} onClick={() => setMode('manual')}>직접입력</button>
      </div>
      )}
      {!isEdit && mode === 'task' && (
        <Field label="업무 검색">
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...formInput, flex: 1 }} value={taskCategory} onChange={e => setTaskCategory(e.target.value)}>
              <option value="">전체 구분</option>
              {taskCategories.map(c => (
                <option key={c.id} value={c.full_label}>{c.short_label || c.full_label}</option>
              ))}
            </select>
            <input style={{ ...formInput, flex: 1.4 }} placeholder="업무 제목 검색" value={taskQuery} onChange={e => setTaskQuery(e.target.value)} />
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, marginBottom: 12 }}>
            {taskOptions.map(t => (
              <div key={t.id} onClick={() => pickTask(t)}
                style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', background: selectedTask?.id === t.id ? '#f0fdf9' : '#fff', borderBottom: '1px solid #f5f5f5' }}>
                {t.title}
              </div>
            ))}
            {taskOptions.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#aaa' }}>검색 결과가 없습니다.</div>}
          </div>
        </Field>
      )}
      <Field label="구분"><input style={formInput} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8 }}>
        <Field label="계약체결일"><input type="date" style={formInput} value={form.contract_date} onChange={e => setForm({ ...form, contract_date: e.target.value })} /></Field>
        <Field label="시작일"><input type="date" style={formInput} value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
        <Field label="종료일"><input type="date" style={formInput} value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
      </div>
      <Field label="프로젝트명"><input style={formInput} value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} /></Field>
      <Field label="고객사"><input style={formInput} value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} /></Field>
      <Field label="견적금액"><input style={formInput} value={moneyFmt(form.quote_amount)} onChange={e => setForm({ ...form, quote_amount: onlyDigits(e.target.value) })} /></Field>
      <Field label="계약금액"><input style={formInput} value={moneyFmt(form.contract_amount)} onChange={e => setForm({ ...form, contract_amount: onlyDigits(e.target.value) })} /></Field>
      <Field label="진행구분">
        <select style={formInput} value={form.progress_type} onChange={e => setForm({ ...form, progress_type: e.target.value })}>
          <option value="">선택</option>
          <option value="사내">사내</option>
          <option value="외주">외주</option>
          <option value="사내+외주">사내+외주</option>
        </select>
      </Field>
      <Field label="비고"><input style={formInput} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={{ ...outlineBtn, flex: 1 }} onClick={onClose}>취소</button>
        <button style={{ ...btn(C.primary), flex: 1 }} disabled={saving} onClick={submit}>{saving ? '저장 중...' : (isEdit ? '수정' : '등록')}</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 거래문서함 — 견적서/거래명세서/출고증/발주서를 발행(우리 발행)/수신(협력사가 보내옴)
// 구분으로 한 화면에서 조회. 발행 건은 각 원본 화면(영업관리/출고관리/발주관리)에서
// 자동으로 집계되고, 수신 건은 이 화면에서 직접 등록(파일첨부)한다.
// ══════════════════════════════════════════════════════════════
const DOC_TYPES = ['견적서', '거래명세서', '출고증', '발주서', '세금계산서'];

// 거래문서함 — 기본은 "거래 세트"(견적서·발주서·출고증·거래명세서·세금계산서를 거래 건 단위로 묶어서 보기)이고,
// 기존의 문서 단위 목록은 "문서 목록" 보기로 전환해서 쓴다.
function DocumentsTab(props) {
  const [view, setView] = useState('sets');
  const tab = (key, label) => (
    <button onClick={() => setView(key)} style={{ ...outlineBtn, padding: '9px 14px', borderRadius: 0,
      background: view === key ? C.primary : '#fff', color: view === key ? '#fff' : '#555', borderColor: view === key ? C.primary : '#ddd' }}>{label}</button>
  );
  const toggle = (
    <div style={{ display: 'flex' }}>
      <span style={{ display: 'inline-flex', borderRadius: 8, overflow: 'hidden' }}>{tab('sets', '거래 세트')}{tab('list', '문서 목록')}</span>
    </div>
  );
  return view === 'sets' ? <TradeSetsView {...props} toggle={toggle} /> : <DocumentListView {...props} toggle={toggle} />;
}

function DocumentListView({ partners, canManage, canDelete, headerSlot, toggle }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ doc_type: '', direction: '', from_date: '', to_date: '', q: '' });
  const [modal, setModal] = useState(null);
  const load = () => API.get('/accounting/documents', { params: filter }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const { sorted, sortKey, sortDir, toggleSort } = useSort(rows, 'doc_date');

  const save = async (formData) => {
    try {
      await API.post('/accounting/inbound-documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModal(null); load();
    } catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const remove = async (row) => {
    if (!window.confirm('이 수신문서를 삭제하시겠습니까?')) return;
    try { await API.delete(`/accounting/inbound-documents/${row.ref_id}`); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const download = (row) => { window.open(`${API.defaults.baseURL}/accounting/inbound-documents/${row.ref_id}/download`, '_blank'); };

  return (
    <Section headerSlot={headerSlot} action={
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {toggle}
        <select style={input} value={filter.doc_type} onChange={e => setFilter({ ...filter, doc_type: e.target.value })}>
          <option value="">전체문서</option>
          {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={input} value={filter.direction} onChange={e => setFilter({ ...filter, direction: e.target.value })}>
          <option value="">발행/수신 전체</option>
          <option value="outbound">발행(우리→거래처)</option>
          <option value="inbound">수신(협력사→우리)</option>
        </select>
        <input type="date" style={input} value={filter.from_date} onChange={e => setFilter({ ...filter, from_date: e.target.value })} />
        <input type="date" style={input} value={filter.to_date} onChange={e => setFilter({ ...filter, to_date: e.target.value })} />
        <input style={input} placeholder="거래처/문서번호 검색" value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} />
        <button style={outlineBtn} onClick={load}>조회</button>
        {canManage && <button style={btn(C.primary)} onClick={() => setModal({ mode: 'add' })}>+ 수신문서등록</button>}
      </div>
    }>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}><Sortable label="문서일자" sortKey="doc_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} /></th>
            <th style={th}>문서종류</th><th style={th}>구분</th><th style={th}>거래처</th><th style={th}>문서번호</th><th style={th}>금액</th><th style={th}>관리</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={7} style={{ ...td, color: '#aaa', padding: 20 }}>조회된 문서가 없습니다.</td></tr>}
          {sorted.map((r, i) => (
            <tr key={i}>
              <td style={td}>{r.doc_date}</td>
              <td style={td}>{r.doc_type}</td>
              <td style={{ ...td, color: r.direction === 'outbound' ? '#185FA5' : '#B85042', fontWeight: 700 }}>
                {r.direction === 'outbound' ? '발행' : '수신'}
              </td>
              <td style={td}>{r.partner_name || '-'}</td>
              <td style={td}>{r.doc_no || '-'}</td>
              <td style={td}>{moneyFmt(r.amount)}</td>
              <td style={td}>
                {r.direction === 'inbound' && r.file_name && <button style={smallBtn('#eee', '#555')} onClick={() => download(r)}>다운로드</button>}
                {r.direction === 'inbound' && canDelete && <>{' '}<button style={smallBtn('#DC2626')} onClick={() => remove(r)}>삭제</button></>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modal && <InboundDocModal partners={partners} onClose={() => setModal(null)} onSave={save} />}
    </Section>
  );
}
function InboundDocModal({ partners, onClose, onSave }) {
  const [form, setForm] = useState({ doc_type: '견적서', doc_date: todayStr(), partner_id: '', partner_name_manual: '', doc_no: '', amount: '', note: '' });
  const [file, setFile] = useState(null);
  const submit = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (file) fd.append('file', file);
    onSave(fd);
  };
  return (
    <Modal title="수신문서등록" width={480} onClose={onClose}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="문서종류">
          <select style={formInput} value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })}>
            {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="문서일자"><input type="date" style={formInput} value={form.doc_date} onChange={e => setForm({ ...form, doc_date: e.target.value })} /></Field>
      </div>
      <Field label="협력사">
        <select style={formInput} value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })}>
          <option value="">직접입력</option>
          {partners.map(p => <option key={p.id} value={p.id}>{p.partner_name}</option>)}
        </select>
      </Field>
      {!form.partner_id && <Field label="협력사 직접입력"><input style={formInput} value={form.partner_name_manual} onChange={e => setForm({ ...form, partner_name_manual: e.target.value })} /></Field>}
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="문서번호"><input style={formInput} value={form.doc_no} onChange={e => setForm({ ...form, doc_no: e.target.value })} /></Field>
        <Field label="금액"><input style={formInput} value={form.amount} onChange={e => setForm({ ...form, amount: onlyDigits(e.target.value) })} /></Field>
      </div>
      <Field label="첨부파일"><FileDropZone fileNames={file ? [file.name] : []} onFiles={fs => setFile(fs[0] || null)} /></Field>
      <Field label="비고"><textarea style={{ ...formInput, minHeight: 50 }} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={outlineBtn} onClick={onClose}>취소</button>
        <button style={btn(C.primary)} onClick={submit}>저장</button>
      </div>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════
// 거래 세트 — 견적서·발주서·출고증·거래명세서·세금계산서를 "한 건의 거래"로 묶어 거래처별로 보여준다.
//   매출(고객사 기준) / 매입(협력사 기준) / 미연결 문서(자동으로 못 묶은 수신문서).
//   조립은 서버(utils/tradeSets.js)가 하고, 이 화면은 거래처 선택 → 거래 건 목록 → 문서 칩(클릭 시 열람).
//   있는 문서는 색칠, 없는 문서는 회색 점선(예: 세금계산서 미발행 건을 한눈에 확인).
// ══════════════════════════════════════════════════════════════
const SET_DOC_COLOR = { 견적서: '#7C3AED', 발주서: '#B45309', 출고증: '#0F6E56', 거래명세서: '#185FA5', 세금계산서: '#DC2626' };
const SET_EXPECTED = { sales: ['견적서', '발주서', '출고증', '거래명세서', '세금계산서'], purchase: ['발주서', '견적서', '거래명세서', '세금계산서'] };
const SET_STATUS_COLOR = { 출고완료: '#0F6E56', 출고대기: '#B45309', 보류: '#DC2626', 견적단계: '#7C3AED', 발주: '#185FA5', 확정: '#0F6E56', 입고완료: '#0F6E56', 취소: '#999' };
const isOpenSettle = (s) => !!(s.settlement && !['수금완료', '지급완료'].includes(s.settlement.status));
const isMissingInvoice = (s, dir) => s.status !== '견적단계' && s.status !== '취소'
  && !s.docs.some(d => d.doc_type === '세금계산서') && (dir === 'purchase' || s.status === '출고완료');

function setDocTarget(d) {
  const base = '/accounting';
  if (d.ref_table === 'quotations') return { mode: 'pdf', url: `${base}/trade-docs/quotation/${d.ref_id}` };
  if (d.ref_table === 'shipments') return { mode: 'pdf', url: `${base}/trade-docs/${d.doc_type === '출고증' ? 'receipt' : 'txspec'}/${d.ref_id}` };
  if (d.ref_table === 'acct_inbound_documents') return d.view === 'download' ? { mode: 'download', url: `${base}/inbound-documents/${d.ref_id}/download` } : null;
  if (d.ref_table === 'acct_purchase_orders') return d.view === 'download' ? { mode: 'download', url: `${base}/trade-docs/po/${d.ref_id}` } : null;
  return null;
}

function TradeSetsView({ canManage, headerSlot, toggle }) {
  const [data, setData] = useState({ sales: [], purchase: [], unlinked: [] });
  const [dir, setDir] = useState('sales');
  const [q, setQ] = useState('');
  const [year, setYear] = useState('');
  const [partner, setPartner] = useState('');
  const [preview, setPreview] = useState(null);
  const [pick, setPick] = useState({});
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/accounting/trade-sets')
      .then(r => setData({ sales: r.data?.sales || [], purchase: r.data?.purchase || [], unlinked: r.data?.unlinked || [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const years = useMemo(() => [...new Set([...data.sales, ...data.purchase].map(s => (s.date || '').slice(0, 4)).filter(Boolean))].sort().reverse(), [data]);
  const sets = dir === 'unlinked' ? [] : data[dir];
  const filtered = useMemo(() => sets.filter(s =>
    (!year || (s.date || '').startsWith(year)) &&
    (!q || [s.partner_name, s.title, s.status].some(v => String(v || '').includes(q)))
  ), [sets, year, q]);

  const NONE = '__none';
  const partners = useMemo(() => {
    const m = new Map();
    filtered.forEach(s => {
      const k = s.partner_key || NONE;
      if (!m.has(k)) m.set(k, { key: k, name: s.partner_name || '(거래처 미지정)', count: 0, last: '', open: 0, noInvoice: 0 });
      const p = m.get(k);
      p.count += 1;
      if ((s.date || '') > p.last) p.last = s.date || '';
      if (isOpenSettle(s)) p.open += 1;
      if (isMissingInvoice(s, dir)) p.noInvoice += 1;
    });
    return [...m.values()].sort((a, b) => (b.last || '').localeCompare(a.last || ''));
  }, [filtered, dir]);

  const shown = useMemo(() => filtered.filter(s => !partner || (s.partner_key || NONE) === partner), [filtered, partner]);
  const summary = useMemo(() => ({
    count: shown.length,
    amount: shown.reduce((a, s) => a + (s.amount || 0), 0),
    open: shown.filter(isOpenSettle).length,
    noInvoice: shown.filter(s => isMissingInvoice(s, dir)).length,
  }), [shown, dir]);

  const openDoc = (d) => {
    const t = setDocTarget(d);
    if (!t) { alert(d.doc_type === '세금계산서' ? `세금계산서 발행 건입니다. (발행일 ${d.date || '-'})` : '열람할 수 있는 파일이 등록되어 있지 않습니다.'); return; }
    // 인증이 Authorization 헤더 방식이므로 blob으로 받아서 열람/저장한다.
    API.get(t.url, { responseType: 'blob' }).then(r => {
      const type = t.mode === 'pdf' ? 'application/pdf' : (r.headers?.['content-type'] || 'application/octet-stream');
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type }));
      if (t.mode === 'pdf') {
        setPreview(prev => { if (prev?.url) URL.revokeObjectURL(prev.url); return { title: `${d.doc_type} ${d.doc_no || ''}`.trim(), url: blobUrl }; });
      } else {
        const a = document.createElement('a');
        a.href = blobUrl; a.download = d.file_name || `${d.doc_type}_${d.doc_no || d.ref_id}`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      }
    }).catch(() => alert('파일을 불러올 수 없습니다. (파일이 없거나 권한이 없습니다)'));
  };
  const downloadInbound = (d) => {
    API.get(`/accounting/inbound-documents/${d.id}/download`, { responseType: 'blob' }).then(r => {
      const blobUrl = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = d.file_name || `문서_${d.id}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    }).catch(() => alert('파일을 불러올 수 없습니다.'));
  };
  const link = async (doc) => {
    const set_key = pick[doc.id];
    if (!set_key) { alert('연결할 거래 세트를 선택해주세요.'); return; }
    try { await API.post('/accounting/trade-links', { set_key, ref_table: 'acct_inbound_documents', ref_id: doc.id }); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };
  const unlink = async (d) => {
    if (!window.confirm('이 문서의 거래 세트 연결을 해제하시겠습니까?')) return;
    try { await API.delete(`/accounting/trade-links/${d.link_id}`); load(); }
    catch (e) { alert(e?.response?.data?.message || '오류가 발생했습니다.'); }
  };

  const dirBtn = (key, label, count) => (
    <button onClick={() => { setDir(key); setPartner(''); }} style={{ ...outlineBtn, padding: '8px 14px',
      background: dir === key ? C.primary : '#fff', color: dir === key ? '#fff' : '#555', borderColor: dir === key ? C.primary : '#ddd' }}>
      {label} <span style={{ opacity: 0.85, marginLeft: 4 }}>{count}</span>
    </button>
  );
  const chip = (d, key, expectedType) => {
    if (!d) {
      return <span key={key} title={`${expectedType} 없음`} style={{ padding: '3px 10px', borderRadius: 14, fontSize: 12.5, border: '1px dashed #ccc', color: '#b5b5b5', background: '#fff' }}>{expectedType}</span>;
    }
    const color = SET_DOC_COLOR[d.doc_type] || '#555';
    return (
      <span key={key} onClick={() => openDoc(d)}
        title={[d.doc_no, d.date, d.amount ? `${moneyFmt(d.amount)}원` : '', d.auto ? '자동 매칭' : ''].filter(Boolean).join(' · ')}
        style={{ padding: '3px 10px', borderRadius: 14, fontSize: 12.5, fontWeight: 600, border: `1px solid ${color}`, color, background: color + '14', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {d.doc_type}{d.auto && <span style={{ fontSize: 10.5, fontWeight: 500, opacity: 0.75 }}>자동</span>}
        {d.link_id && canManage && <span onClick={(e) => { e.stopPropagation(); unlink(d); }} title="연결 해제" style={{ marginLeft: 2, fontSize: 11, color: '#999' }}>✕</span>}
      </span>
    );
  };

  const action = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {toggle}
      <select style={input} value={year} onChange={e => setYear(e.target.value)}>
        <option value="">전체 기간</option>
        {years.map(y => <option key={y} value={y}>{y}년</option>)}
      </select>
      <input style={input} placeholder="거래처/품목 검색" value={q} onChange={e => setQ(e.target.value)} />
      <button style={outlineBtn} onClick={load}>{loading ? '조회 중...' : '새로고침'}</button>
    </div>
  );

  return (
    <Section headerSlot={headerSlot} action={action}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {dirBtn('sales', '매출 (고객사)', data.sales.length)}
        {dirBtn('purchase', '매입 (협력사)', data.purchase.length)}
        {dirBtn('unlinked', '미연결 문서', data.unlinked.length)}
      </div>

      {dir === 'unlinked' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>문서일자</th><th style={th}>문서종류</th><th style={th}>거래처</th><th style={th}>문서번호</th><th style={th}>금액</th>
            <th style={th}>연결할 거래 세트</th><th style={th}>관리</th>
          </tr></thead>
          <tbody>
            {data.unlinked.length === 0 && <tr><td colSpan={7} style={{ ...td, color: '#aaa', padding: 20 }}>모든 수신문서가 거래 세트에 연결되어 있습니다.</td></tr>}
            {data.unlinked.map(d => (
              <tr key={d.id}>
                <td style={td}>{d.doc_date}</td>
                <td style={{ ...td, color: SET_DOC_COLOR[d.doc_type], fontWeight: 600 }}>{d.doc_type}</td>
                <td style={td}>{d.partner_name || '-'}</td>
                <td style={td}>{d.doc_no || '-'}</td>
                <td style={td}>{moneyFmt(d.amount)}</td>
                <td style={td}>
                  {d.candidates.length === 0 ? <span style={{ color: '#aaa' }}>같은 거래처의 거래 세트 없음</span> : (
                    <select style={{ ...input, padding: '6px 8px', maxWidth: 360 }} value={pick[d.id] || ''} onChange={e => setPick({ ...pick, [d.id]: e.target.value })}>
                      <option value="">선택</option>
                      {d.candidates.map(c => <option key={c.set_key} value={c.set_key}>[{c.direction === 'sales' ? '매출' : '매입'}] {c.label} · {moneyFmt(c.amount)}원</option>)}
                    </select>
                  )}
                </td>
                <td style={td}>
                  {d.file_name && <><button style={smallBtn('#888')} onClick={() => downloadInbound(d)}>열람</button>{' '}</>}
                  {canManage && d.candidates.length > 0 && <button style={smallBtn(C.primary)} onClick={() => link(d)}>연결</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ width: 270, flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 10, maxHeight: '68vh', overflowY: 'auto' }}>
            <div onClick={() => setPartner('')} style={{ padding: '11px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: !partner ? '#E8F5F0' : '#fff', fontWeight: 700, fontSize: 14 }}>
              전체 거래처 <span style={{ color: C.sub, fontWeight: 500, fontSize: 13 }}>({partners.length}곳 · {filtered.length}건)</span>
            </div>
            {partners.length === 0 && <div style={{ padding: 18, color: '#aaa', fontSize: 13.5, textAlign: 'center' }}>조회된 거래가 없습니다.</div>}
            {partners.map(p => (
              <div key={p.key} onClick={() => setPartner(p.key)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3', background: partner === p.key ? '#E8F5F0' : '#fff' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text, wordBreak: 'keep-all' }}>{p.name}</div>
                <div style={{ fontSize: 12.5, color: C.sub, marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span>{p.count}건</span><span>최근 {p.last || '-'}</span>
                  {p.open > 0 && <span style={{ color: '#B45309', fontWeight: 600 }}>{dir === 'sales' ? '미수' : '미지급'} {p.open}</span>}
                  {p.noInvoice > 0 && <span style={{ color: '#DC2626', fontWeight: 600 }}>계산서 없음 {p.noInvoice}</span>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              {[
                ['거래 건수', `${summary.count}건`, C.text],
                ['거래 금액 (VAT 포함)', `${moneyFmt(summary.amount)}원`, C.text],
                [dir === 'sales' ? '미수금 건' : '미지급 건', `${summary.open}건`, summary.open ? '#B45309' : C.text],
                ['세금계산서 없는 건', `${summary.noInvoice}건`, summary.noInvoice ? '#DC2626' : C.text],
              ].map(([label, val, color]) => (
                <div key={label} style={{ flex: '1 1 150px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: 12.5, color: C.sub }}>{label}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color, marginTop: 2 }}>{val}</div>
                </div>
              ))}
            </div>

            <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, maxHeight: '58vh', overflowY: 'auto' }}>
              {shown.length === 0 && <div style={{ padding: 26, color: '#aaa', textAlign: 'center', fontSize: 14 }}>조회된 거래 건이 없습니다.</div>}
              {shown.map(s => {
                const expected = s.status === '견적단계' ? ['견적서'] : SET_EXPECTED[dir];
                const extra = s.docs.filter(d => !expected.includes(d.doc_type));
                const sc = SET_STATUS_COLOR[s.status] || '#555';
                return (
                  <div key={s.set_key} style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13.5, color: C.sub, minWidth: 82 }}>{s.date || '-'}</span>
                      {!partner && <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.partner_name || '-'}</span>}
                      <span style={{ fontSize: 14, color: C.text, flex: 1, minWidth: 120 }}>{s.title}{s.qty ? <span style={{ color: C.sub }}> · {moneyFmt(s.qty)}개</span> : null}</span>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{moneyFmt(s.amount)}원</span>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: sc, border: `1px solid ${sc}`, borderRadius: 12, padding: '2px 10px' }}>{s.status}</span>
                      {s.settlement && (
                        <span style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 12, padding: '2px 10px',
                          color: isOpenSettle(s) ? '#B45309' : '#0F6E56', background: isOpenSettle(s) ? '#FEF3C7' : '#DCFCE7' }}>{s.settlement.label || s.settlement.status}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap', alignItems: 'center' }}>
                      {expected.map(t => {
                        const ds = s.docs.filter(d => d.doc_type === t);
                        return ds.length ? ds.map((d, i) => chip(d, `${t}${i}`, t)) : chip(null, t, t);
                      })}
                      {extra.map((d, i) => chip(d, `x${i}`, d.doc_type))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {preview && (
        <Modal title={preview.title} width={920} onClose={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>
          <iframe title="doc" src={preview.url} style={{ width: '100%', height: '72vh', border: '1px solid #eee', borderRadius: 8 }} />
        </Modal>
      )}
    </Section>
  );
}
